// mobile/screens/OperationsScreen.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Alert,
  AppState,
  Platform,
  Modal,
  Image,
  LayoutAnimation,
  UIManager,
} from 'react-native';
import axios from 'axios';
import { API_BASE } from '../utils';
import { useFocusEffect } from '@react-navigation/native';
import * as ImagePicker from 'expo-image-picker';
import PadiProIcon from '../components/PadiProIcon';
import EnterpriseHeader from '../components/EnterpriseHeader';
import { PADIPRO, padiproShadow } from '../components/padiproTheme';
import { useResponsive } from '../components/responsive';

const AUTO_REFRESH_MS = 15000;
const DEFAULT_CYCLE_DAYS = 12;
const MAX_OPERATION_PHOTOS = 8;
const RICE_VARIETY_OPTIONS = ['Logawa Janger', 'Logawa Batu', 'Cibatu Putih', 'Seibatu 06'];
const RICE_NOTE_PHASE_OPTIONS = [
  { key: 'anytime', label: 'Kapan saja' },
  { key: 'select_seed', label: 'Pilih bibit' },
  { key: 'planting', label: 'Mulai tanam' },
  { key: 'hst', label: 'Rentang HST' },
];
const RICE_NOTE_PRIORITY_OPTIONS = [
  { key: 'normal', label: 'Biasa' },
  { key: 'important', label: 'Penting' },
  { key: 'urgent', label: 'Mendesak' },
];

const riceNotePhaseLabel = (note) => {
  if (note?.trigger_phase === 'select_seed') return 'Saat pilih bibit';
  if (note?.trigger_phase === 'planting') return 'Saat mulai tanam';
  if (note?.trigger_phase === 'hst') return `HST ${note?.hst_from ?? 0}–${note?.hst_to ?? 0}`;
  return 'Kapan saja';
};

const normalizeVarietyKey = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

function VarietySelector({ value, onChange }) {
  const key = normalizeVarietyKey(value);
  const selected = RICE_VARIETY_OPTIONS.find((name) => normalizeVarietyKey(name) === key);
  const ambiguousFamily = ['logawa', 'cibatu', 'seibatu'].includes(key);
  const showReview = !!key && !selected;

  return (
    <View style={s.varietyPicker}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.varietyOptions}>
        {RICE_VARIETY_OPTIONS.map((name) => {
          const active = normalizeVarietyKey(name) === key;
          return (
            <TouchableOpacity
              key={name}
              style={[s.varietyOption, active && s.varietyOptionActive]}
              onPress={() => onChange(name)}
            >
              <Text style={[s.varietyOptionText, active && s.varietyOptionTextActive]}>{name}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
      {showReview && (
        <Text style={s.varietyReviewText}>
          {ambiguousFamily
            ? `“${value}” belum lengkap. Pilih jenisnya agar catatan tidak tertukar.`
            : 'Belum ada di daftar baku. Tetap bisa disimpan, tetapi periksa kembali ejaannya.'}
        </Text>
      )}
    </View>
  );
}

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

const T = {
  bg: PADIPRO.bg,
  card: PADIPRO.surface,
  text: PADIPRO.text,
  muted: PADIPRO.muted,
  border: PADIPRO.border,

  green: PADIPRO.primary,
  greenDark: PADIPRO.primaryDark,
  greenSoft: PADIPRO.primarySoft,
  greenSoft2: PADIPRO.primarySoftStrong,

  warn: PADIPRO.warning,
  warnSoft: PADIPRO.warningSoft,

  danger: PADIPRO.danger,
  dangerSoft: PADIPRO.dangerSoft,
  riceGold: PADIPRO.gold,
  riceSoft: PADIPRO.goldSoft,
};

const ACT = {
  semprot: { glyph: 'semprot', dot: '#2F7D5A', bg: '#E9F1EB', border: '#CFE0D2' },
  pupuk: { glyph: 'pupuk', dot: PADIPRO.primary, bg: PADIPRO.primarySoft, border: PADIPRO.primarySoftStrong },
  air: { glyph: 'air', dot: PADIPRO.sky, bg: PADIPRO.skySoft, border: '#C9E1E8' },
  gulma: { glyph: 'gulma', dot: '#5B6B2F', bg: '#EEF1E2', border: '#DCE2C6' },
  catatan: { glyph: 'catatan', dot: PADIPRO.muted, bg: PADIPRO.surfaceSunken, border: PADIPRO.border },
  foto: { glyph: 'foto', dot: PADIPRO.primaryDark, bg: '#EDF3EC', border: '#DCE6DA' },
  panen: { glyph: 'panen', dot: PADIPRO.goldDeep, bg: PADIPRO.goldSoft, border: '#E8D3A0' },
  default: { glyph: 'default', dot: PADIPRO.muted, bg: PADIPRO.surfaceSunken, border: PADIPRO.border },
};

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const formatIDR = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

const prettyCategory = (v) => {
  const raw = String(v || '').trim();
  if (!raw) return '';
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
};

const summarizeCostCategory = (field, expenseItems = []) => {
  const fromItems = Array.from(
    new Set(
      (Array.isArray(expenseItems) ? expenseItems : [])
        .map((it) => prettyCategory(it?.category))
        .filter(Boolean)
    )
  );

  if (fromItems.length === 1) return fromItems[0];
  if (fromItems.length === 2) return `${fromItems[0]} + ${fromItems[1]}`;
  if (fromItems.length > 2) return `${fromItems[0]} +${fromItems.length - 1} lainnya`;

  const fallback = [
    field?.expense_category,
    field?.cost_category,
    field?.latest_expense_category,
    field?.last_expense_category,
    field?.top_expense_category,
  ]
    .map(prettyCategory)
    .find(Boolean);

  return fallback || null;
};

const notify = (title, message) => {
  const msg = String(message || '');
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined' && window.alert) window.alert(`${title}\n\n${msg}`);
    else console.log(`[${title}] ${msg}`);
  } else {
    Alert.alert(title, msg);
  }
};

const STAGE_BANDS = [
  { key: 'vegetatif', label: 'Vegetatif', glyph: 'stage-vegetatif', color: '#4A9B6E', from: 0.0, to: 0.28 },
  { key: 'bunting', label: 'Bunting', glyph: 'stage-bunting', color: '#2F7D5A', from: 0.28, to: 0.45 },
  { key: 'heading', label: 'Malai', glyph: 'stage-heading', color: PADIPRO.primary, from: 0.45, to: 0.62 },
  { key: 'filling', label: 'Isi Bulir', glyph: 'stage-filling', color: '#8A7A2E', from: 0.62, to: 0.88 },
  { key: 'ripening', label: 'Menguning', glyph: 'stage-ripening', color: PADIPRO.gold, from: 0.88, to: 1.0 },
];

const clamp01 = (x) => Math.max(0, Math.min(1, x));

function ModalSheet({ stylesRef, children, cardStyle = null }) {
  return (
    <View style={stylesRef.modalMask}>
      <View style={[stylesRef.modalCard, cardStyle]}>
        <View style={stylesRef.modalContentNoScroll}>{children}</View>
      </View>
    </View>
  );
}

function percentOfTarget(plantAge, targetDays) {
  if (!(targetDays > 0) || !(plantAge >= 0)) return 0;
  return clamp01(plantAge / targetDays);
}

function getStageByPercent(p) {
  for (const band of STAGE_BANDS) {
    if (p >= band.from && p < band.to) return band;
  }
  return STAGE_BANDS[STAGE_BANDS.length - 1];
}

function ProgressHarvest({ plantAge = 0, targetDays = 100, plantDate = null, harvestAt = null }) {
  const p = percentOfTarget(plantAge, targetDays);
  const active = getStageByPercent(p);
  const daysLeft = targetDays > 0 ? Math.max(0, Math.ceil(targetDays - plantAge)) : null;

  const fmt = (d) => {
    if (!d) return '—';
    const dt = d instanceof Date ? d : new Date(d);
    return dt.toLocaleDateString('id-ID', { day: '2-digit', month: 'short' });
  };

  return (
    <View style={{ marginTop: 10 }}>
      <View style={s.phTop}>
        <View style={s.phPill}>
          <Text style={s.phPillText}>{Math.round(p * 100)}%</Text>
        </View>
        <View style={s.phStage}>
          <PadiProIcon name={active.glyph} size={15} color={active.color} strokeWidth={2} />
          <Text style={s.phStageText}>{active.label}</Text>
        </View>
        <Text style={s.phMeta}>
          HST <Text style={s.phMetaStrong}>{plantAge}</Text> / {targetDays}h
        </Text>
      </View>

      <View style={s.phBar}>
        <View style={[s.phFill, { width: `${p * 100}%`, backgroundColor: active.color }]} />
      </View>

      <View style={s.phBottom}>
        <Text style={s.phBottomText}>
          Tanam: <Text style={s.phBottomStrong}>{fmt(plantDate)}</Text>
        </Text>
        <Text style={s.phBottomText}>
          Est. Panen: <Text style={s.phBottomStrong}>{fmt(harvestAt)}</Text>
          {daysLeft != null ? ` · ${daysLeft}h lagi` : ''}
        </Text>
      </View>
    </View>
  );
}

export default function OperationsScreen({ route, navigation }) {
  const token = route?.params?.token;
  const user = route?.params?.user;

  const authHdr = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const { bottomInset, contentPadding, contentWidthStyle } = useResponsive();

  if (!token) {
    return (
      <View style={[s.center, { backgroundColor: T.bg }]}>
        <Text style={{ fontWeight: '900', color: T.text, fontSize: 16 }}>Operasional</Text>
        <Text style={{ color: T.muted, marginTop: 8, textAlign: 'center', fontWeight: '600' }}>
          Token belum ada. Buka screen ini dari flow login/scan yang mengirim token.
        </Text>
      </View>
    );
  }

  const mountedRef = useRef(true);
  const intervalRef = useRef(null);
  const appState = useRef(AppState.currentState);

  const [fields, setFields] = useState([]);
  const [allFields, setAllFields] = useState([]);
  const [archivedFields, setArchivedFields] = useState([]);
  const [showArchivedFields, setShowArchivedFields] = useState(false);
  const [archiveSaving, setArchiveSaving] = useState({});
  const [drafts, setDrafts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorText, setErrorText] = useState('');

  const [seasons, setSeasons] = useState([]);
  const [seasonLoading, setSeasonLoading] = useState(false);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [showSeasonFilter, setShowSeasonFilter] = useState(false);

  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('nursery');

  const [openHistory, setOpenHistory] = useState({});
  const [histTab, setHistTab] = useState({});
  const [histLoading, setHistLoading] = useState({});
  const [histItems, setHistItems] = useState({});

  const [expLoading, setExpLoading] = useState({});
  const [expData, setExpData] = useState({});

  const [draftSaving, setDraftSaving] = useState({});

  const [imgPreview, setImgPreview] = useState({ open: false, uris: [], index: 0, title: '', subtitle: '' });

  const [showAddField, setShowAddField] = useState(false);
  const [nfName, setNfName] = useState('');
  const [nfSeed, setNfSeed] = useState('');
  const [nfPlant, setNfPlant] = useState(() => new Date().toISOString().slice(0, 10));
  const [nfHarvestDays, setNfHarvestDays] = useState('100');
  const [creating, setCreating] = useState(false);

  const [showActivity, setShowActivity] = useState(false);
  const [actField, setActField] = useState(null);
  const [actType, setActType] = useState('semprot');
  const [actSubtype, setActSubtype] = useState('');
  const [actDate, setActDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [actNote, setActNote] = useState('');
  const [actQty, setActQty] = useState('');
  const [actUnit, setActUnit] = useState('');
  const [actSaving, setActSaving] = useState(false);

  const [showStartCycle, setShowStartCycle] = useState(false);
  const [scField, setScField] = useState(null);
  const [scPlant, setScPlant] = useState(() => new Date().toISOString().slice(0, 10));
  const [scHarvestDays, setScHarvestDays] = useState('100');
  const [scCycleDays, setScCycleDays] = useState(String(DEFAULT_CYCLE_DAYS));
  const [scNotes, setScNotes] = useState('');
  const [scSeedVariety, setScSeedVariety] = useState('');
  const [scSaving, setScSaving] = useState(false);

  const [showNursery, setShowNursery] = useState(false);
  const [nsField, setNsField] = useState(null);
  const [nsDate, setNsDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [nsReadyDays, setNsReadyDays] = useState('14');
  const [nsSeedVariety, setNsSeedVariety] = useState('');
  const [nsNote, setNsNote] = useState('');
  const [fieldPickerQuery, setFieldPickerQuery] = useState('');
  const [nsSaving, setNsSaving] = useState(false);

  const [showPhoto, setShowPhoto] = useState(false);
  const [phField, setPhField] = useState(null);
  const [phDate, setPhDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [phNote, setPhNote] = useState('');
  const [phAssets, setPhAssets] = useState([]);
  const [phUploading, setPhUploading] = useState(false);

  const [showAddExpense, setShowAddExpense] = useState(false);
  const [exField, setExField] = useState(null);
  const [exDate, setExDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [exCategory, setExCategory] = useState('Pupuk');
  const [exSubcategory, setExSubcategory] = useState('');
  const [exItem, setExItem] = useState('');
  const [exQty, setExQty] = useState('');
  const [exUnit, setExUnit] = useState('');
  const [exUnitPrice, setExUnitPrice] = useState('');
  const [exTotal, setExTotal] = useState('');
  const [exNotes, setExNotes] = useState('');
  const [exSaving, setExSaving] = useState(false);

  const [riceNotes, setRiceNotes] = useState([]);
  const [riceNotesExpanded, setRiceNotesExpanded] = useState(false);
  const [showAddRiceNote, setShowAddRiceNote] = useState(false);
  const [rnTitle, setRnTitle] = useState('');
  const [rnBody, setRnBody] = useState('');
  const [rnVariety, setRnVariety] = useState('');
  const [rnPhase, setRnPhase] = useState('anytime');
  const [rnHstFrom, setRnHstFrom] = useState('0');
  const [rnHstTo, setRnHstTo] = useState('30');
  const [rnPriority, setRnPriority] = useState('important');
  const [rnFieldId, setRnFieldId] = useState(null);
  const [rnSaving, setRnSaving] = useState(false);

  const isAnyFormModalOpen =
    showAddField || showArchivedFields || showActivity || showNursery || showStartCycle || showPhoto || showAddExpense || showAddRiceNote;

  const toYMD = (d) => {
    const dt = d ? new Date(d) : new Date();
    if (Number.isNaN(dt.getTime())) return null;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };

  const fmtDateShort = (d) => {
    if (!d) return '—';
    const dt = d instanceof Date ? d : new Date(d);
    return dt.toLocaleDateString('id-ID', { day: '2-digit', month: 'short' });
  };

  const fmtDateTime = (d) => {
    if (!d) return '—';
    const dt = d instanceof Date ? d : new Date(d);
    return dt.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getSeasonName = (season) => {
    if (!season) return 'Musim';
    const name = String(season.name || '').trim();
    return name || `Musim ${season.season_id}`;
  };

  const getSeasonRangeLabel = (season) => {
    if (!season?.start_date) return 'Rentang tanggal belum tersedia';
    const start = fmtDateShort(season.start_date);
    const end = season.end_date ? fmtDateShort(season.end_date) : 'Berjalan';
    return `${start} - ${end}`;
  };

  const normalizeSeasonId = (value) => {
    const sid = String(value ?? '').trim();
    return sid || null;
  };

  const selectedSeason = useMemo(
    () => seasons.find((x) => String(x.season_id) === String(selectedSeasonId)) || null,
    [seasons, selectedSeasonId]
  );
  const activeSeason = useMemo(
    () => seasons.find((x) => x && x.end_date == null) || null,
    [seasons]
  );

  const parseNum = (v) => {
    const s = String(v ?? '').trim();
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  const daysBetween = (fromISO, toISO = new Date()) => {
    if (!fromISO) return null;
    const a = new Date(fromISO);
    const b = new Date(toISO);
    a.setHours(0, 0, 0, 0);
    b.setHours(0, 0, 0, 0);
    return Math.floor((b - a) / 86400000);
  };

  const addDays = (fromISO, d) => {
    if (!fromISO || !Number.isFinite(d)) return null;
    const dt = new Date(fromISO);
    dt.setDate(dt.getDate() + d);
    return dt;
  };

  const daysUntil = (toDate) => {
    if (!toDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(toDate);
    end.setHours(0, 0, 0, 0);
    return Math.ceil((end - today) / 86400000);
  };

  const resolvePhase = (f) => {
    const toTs = (d) => {
      if (!d) return null;
      const ts = new Date(d).getTime();
      return Number.isFinite(ts) ? ts : null;
    };

    const cycleStatus = String(f?.cycle_status || '').toLowerCase();

    const hss = Number.isFinite(Number(f?.nurseryAge)) ? Number(f.nurseryAge) : null;
    const readyManual = !!f?.ready_confirmed_at;
    const readyDays = Number.isFinite(Number(f?.hss_ready_days)) ? Number(f.hss_ready_days) : 14;
    const nurseryTs = toTs(f?.nursery_date);
    const plantTs = toTs(f?.plant_date);
    const nurseryPriority = nurseryTs != null && (plantTs == null || nurseryTs >= plantTs);
    const plantedAfterNursery = plantTs != null && (nurseryTs == null || plantTs >= nurseryTs);

    if (plantedAfterNursery && cycleStatus === 'active') return 'running';

    // Jika tanggal tabur bibit lebih baru dari tanggal tanam terakhir, prioritaskan fase nursery/ready.
    if (nurseryPriority) {
      if (readyManual) return 'ready';
      if (hss != null && hss >= readyDays) return 'ready';
      return 'nursery';
    }

    if (nurseryTs != null && readyManual) return 'ready';
    if (nurseryTs != null && hss != null && hss >= readyDays) return 'ready';

    if (cycleStatus === 'closed') return 'harvest';
    if (cycleStatus === 'active' || f?.plant_date) return 'running';

    if (readyManual) return 'ready';
    if (hss != null && hss >= readyDays) return 'ready';
    return 'nursery';
  };

  const ActivityStyle = (t) => ACT[String(t || '').toLowerCase()] || ACT.default;

  const groupHistoryItems = useCallback((items = [], plantDate = null) => {
    const groups = [];

    for (const it of items) {
      const type = String(it.activity_type || '').toLowerCase();

      const dt = it.activity_date ? new Date(it.activity_date) : null;
      const calculatedHst = plantDate && it.activity_date ? daysBetween(plantDate, it.activity_date) : null;
      const observationHst = Number.isFinite(calculatedHst) && calculatedHst >= 0 ? calculatedHst : null;
      const timeKey =
        dt && !Number.isNaN(dt.getTime())
          ? `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(
              2,
              '0'
            )} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`
          : String(it.activity_date || '');

      const groupKey = `${type}__${timeKey}`;

      const parts = [];
      if (it.activity_subtype) parts.push(it.activity_subtype);
      if (it.product_or_note) parts.push(it.product_or_note);
      if (it.qty != null) parts.push(`${it.qty}${it.qty_unit ? ` ${it.qty_unit}` : ''}`);

      const detailText = parts.join(' · ').trim();

      const last = groups[groups.length - 1];

      if (last && last._groupKey === groupKey) {
        last.items.push(it);
        if (detailText) last.lines.push(detailText);

        if (it.photo_path) {
          last.photos.push(`${API_BASE}${it.photo_path}?v=${encodeURIComponent(it.event_id || it.created_at || it.photo_path)}`);
        }
      } else {
        groups.push({
          _groupKey: groupKey,
          type,
          title: String(it.activity_type || '').toUpperCase(),
          dateLabel:
            dt && !Number.isNaN(dt.getTime())
              ? dt.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })
              : String(it.activity_date || '—'),
          ageLabel: observationHst != null ? observationHst + ' HST' : null,
          lines: detailText ? [detailText] : [],
          items: [it],
          photos: it.photo_path ? [`${API_BASE}${it.photo_path}?v=${encodeURIComponent(it.event_id || it.created_at || it.photo_path)}`] : [],
        });
      }
    }

    return groups.map((g) => ({
      ...g,
      lines: Array.from(new Set(g.lines.filter(Boolean))),
      photoUris: Array.from(new Set((g.photos || []).filter(Boolean))),
      photoUri: g.photos?.[0] || null,
      count: g.items.length,
    }));
  }, []);

  const pickCostTotal = (f) => {
    const candidates = [
      f?.expense_total_active,
      f?.expense_total,
      f?.total_expense,
      f?.expenses_total,
      f?.cost_total,
      f?.total_cost,
      f?.cost_idr,
      f?.sum_expenses,
      f?.cycle_cost_total,
      f?.season_cost_total,
    ];
    for (const c of candidates) {
      const n = safeNum(c);
      if (n !== null) return n;
    }
    return null;
  };

  const pickRante = (f) => {
    const candidates = [f?.rante, f?.area_rante, f?.rante_size, f?.luas_rante];
    for (const c of candidates) {
      const n = safeNum(c);
      if (n !== null && n > 0) return n;
    }
    return null;
  };

  const fetchSeasons = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) setSeasonLoading(true);
        const r = await axios.get(`${API_BASE}/seasons`, { headers: authHdr });
        if (!mountedRef.current) return null;

        const list = Array.isArray(r.data) ? r.data : [];
        setSeasons(list);

        const active = list.find((x) => x && x.end_date == null);
        const fallbackSid = normalizeSeasonId(active?.season_id || list?.[0]?.season_id);
        const hasSelected = selectedSeasonId && list.some((x) => String(x.season_id) === String(selectedSeasonId));

        if (!hasSelected) {
          setSelectedSeasonId(fallbackSid);
          return fallbackSid;
        }

        return normalizeSeasonId(selectedSeasonId);
      } catch (e) {
        if (!mountedRef.current) return null;
        const msg = e?.response?.data?.error || e?.message || 'Gagal memuat musim';
        setErrorText(msg);
        return null;
      } finally {
        if (mountedRef.current) setSeasonLoading(false);
      }
    },
    [authHdr, selectedSeasonId]
  );

  const fetchFieldsSummaryCompat = async (signal, seasonId = null) => {
    const params = new URLSearchParams();
    if (seasonId) {
      params.set('season', 'by_id');
      params.set('season_id', String(seasonId));
    } else {
      params.set('season', 'active');
    }

    try {
      const r = await axios.get(`${API_BASE}/operations/fields-summary?${params.toString()}`, {
        headers: authHdr,
        signal,
      });
      return r.data;
    } catch {
      const r2 = await axios.get(`${API_BASE}/operations/fields?${params.toString()}`, {
        headers: authHdr,
        signal,
      });
      return r2.data;
    }
  };

  const fetchAll = useCallback(
    async ({ silent = false, seasonIdOverride = null } = {}) => {
      const controller = new AbortController();
      try {
        if (!silent) setLoading(true);
        setErrorText('');

        const sid = normalizeSeasonId(seasonIdOverride ?? selectedSeasonId);
        const [fsData, ds, fm, rn, af] = await Promise.all([
          fetchFieldsSummaryCompat(controller.signal, sid),
          axios.get(`${API_BASE}/operations/spray-logs/drafts?range=2d`, {
            headers: authHdr,
            signal: controller.signal,
          }),
          axios.get(`${API_BASE}/operations/fields-min`, {
            headers: authHdr,
            signal: controller.signal,
          }),
          axios
            .get(`${API_BASE}/operations/rice-notes`, {
              headers: authHdr,
              signal: controller.signal,
            })
            .catch(() => ({ data: { items: [] } })),
          axios
            .get(`${API_BASE}/operations/fields-archived`, {
              headers: authHdr,
              signal: controller.signal,
            })
            .catch(() => ({ data: { fields: [] } })),
        ]);

        if (!mountedRef.current) return;

        const list = Array.isArray(fsData?.fields)
          ? fsData.fields
          : Array.isArray(fsData?.items)
          ? fsData.items
          : [];

        const derived = list.map((f) => {
          const id = f.id ?? f.field_id;
          const cycle = Number.isFinite(f.cycle_days) ? f.cycle_days : DEFAULT_CYCLE_DAYS;
          const targetHarvest = Number.isFinite(f.harvest_days) ? f.harvest_days : 100;
          const readyDays = Number.isFinite(Number(f.hss_ready_days)) ? Number(f.hss_ready_days) : 14;
          const nurseryAge = f.nursery_date ? daysBetween(f.nursery_date) : null;
          const readyAt = f.nursery_date ? addDays(f.nursery_date, readyDays) : null;

          const plantTs = f.plant_date ? new Date(f.plant_date).getTime() : NaN;
          const lastSprayAt = f.effective_last_spray_at || f.last_spray_at || null;
          const sprayTs = lastSprayAt ? new Date(lastSprayAt).getTime() : NaN;
          const sprayBaseDate =
            Number.isFinite(sprayTs) && Number.isFinite(plantTs) && sprayTs >= plantTs
              ? lastSprayAt
              : f.plant_date;
          const dueDate = addDays(sprayBaseDate, cycle);
          const dLeft = daysUntil(dueDate);

          const plantAge = f.plant_date ? daysBetween(f.plant_date) : null;
          const harvestAt = f.plant_date ? addDays(f.plant_date, targetHarvest) : null;

          let status = 'ontrack';
          if (dLeft !== null) {
            if (dLeft < 0) status = 'overdue';
            else if (dLeft <= 2) status = 'due';
          }

          const cycleStatus = f.cycle_status ? String(f.cycle_status).toLowerCase() : 'active';

          const costTotal = pickCostTotal(f);
          const rante = pickRante(f);

          const mapped = {
            ...f,
            id,
            cycle_days: cycle,
            harvest_days: targetHarvest,
            hss_ready_days: readyDays,
            nurseryAge,
            readyAt,
            dueDate,
            dLeft,
            status,
            plantAge,
            harvestAt,
            last_spray_at: lastSprayAt,
            effective_last_spray_at: lastSprayAt,
            last_spray_source: f.last_spray_source || null,
            cycle_status: cycleStatus,
            cost_total: costTotal,
            rante,
            cost_per_rante: costTotal != null && rante != null ? costTotal / rante : null,
          };

          return {
            ...mapped,
            phase: resolvePhase(mapped),
          };
        });

        setFields(derived);
        setAllFields(
          Array.isArray(fm?.data?.fields)
            ? fm.data.fields
                .map((f) => ({
                  ...f,
                  id: Number(f?.id ?? f?.field_id),
                }))
                .filter((f) => Number.isFinite(f.id))
            : []
        );
        setArchivedFields(Array.isArray(af?.data?.fields) ? af.data.fields : []);
        setDrafts(Array.isArray(ds.data?.drafts) ? ds.data.drafts : []);
        setRiceNotes(Array.isArray(rn?.data?.items) ? rn.data.items : []);
      } catch (e) {
        if (!mountedRef.current) return;
        const msg = e?.response?.data?.error || e?.message || 'Gagal memuat data';
        setErrorText(msg);
        notify('Error', msg);
      } finally {
        if (!mountedRef.current) return;
        setLoading(false);
        setRefreshing(false);
      }

      return () => controller.abort();
    },
    [authHdr, selectedSeasonId]
  );

  const fetchHistory = async (fieldId, seasonIdOverride = null) => {
    try {
      setHistLoading((m) => ({ ...m, [fieldId]: true }));
      const sid = normalizeSeasonId(seasonIdOverride ?? selectedSeasonId);
      const q = new URLSearchParams({ limit: '250' });
      q.set('season', sid || 'active');

      const r = await axios.get(`${API_BASE}/operations/fields/${fieldId}/events?${q.toString()}`, {
        headers: authHdr,
      });
      const items = Array.isArray(r.data?.items) ? r.data.items : [];
      setHistItems((m) => ({ ...m, [fieldId]: items }));
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Gagal memuat riwayat';
      notify('Error', msg);
      setHistItems((m) => ({ ...m, [fieldId]: [] }));
    } finally {
      setHistLoading((m) => ({ ...m, [fieldId]: false }));
    }
  };

  const fetchFieldExpenses = async (fieldId, seasonIdOverride = null) => {
    try {
      setExpLoading((m) => ({ ...m, [fieldId]: true }));
      const sid = normalizeSeasonId(seasonIdOverride ?? selectedSeasonId);
      const q = new URLSearchParams({ limit: '300' });
      if (sid) {
        q.set('season', 'by_id');
        q.set('season_id', sid);
      } else {
        q.set('season', 'active');
      }

      const r = await axios.get(`${API_BASE}/operations/fields/${fieldId}/expenses?${q.toString()}`, {
        headers: authHdr,
      });
      setExpData((m) => ({
        ...m,
        [fieldId]: {
          total: Number(r.data?.total || 0),
          items: Array.isArray(r.data?.items) ? r.data.items : [],
        },
      }));
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Gagal memuat biaya';
      notify('Error', msg);
      setExpData((m) => ({ ...m, [fieldId]: { total: 0, items: [] } }));
    } finally {
      setExpLoading((m) => ({ ...m, [fieldId]: false }));
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      const sid = await fetchSeasons();
      await fetchAll({ seasonIdOverride: sid });
    })();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchAll, fetchSeasons]);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      if (!isAnyFormModalOpen) fetchAll({ silent: true });
    }, AUTO_REFRESH_MS);
    return () => clearInterval(intervalRef.current);
  }, [fetchAll, isAnyFormModalOpen]);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      (async () => {
        const sid = await fetchSeasons({ silent: true });
        if (!cancelled) await fetchAll({ silent: true, seasonIdOverride: sid });
      })();
      return () => {
        cancelled = true;
      };
    }, [fetchAll, fetchSeasons])
  );

  useEffect(() => {
    const sub = AppState.addEventListener('change', (next) => {
      const prev = appState.current;
      appState.current = next;
      if (prev?.match(/inactive|background/) && next === 'active' && !isAnyFormModalOpen) {
        fetchAll({ silent: true });
      }
    });
    return () => sub.remove();
  }, [fetchAll, isAnyFormModalOpen]);

  const onRefresh = async () => {
    setRefreshing(true);
    const sid = await fetchSeasons({ silent: true });
    await fetchAll({ seasonIdOverride: sid });
  };

  useEffect(() => {
    if (!mountedRef.current) return;
    setOpenHistory({});
    setHistTab({});
    setHistLoading({});
    setHistItems({});
    setExpLoading({});
    setExpData({});
    fetchAll({ silent: true });
  }, [selectedSeasonId, fetchAll]);

  const phaseCounts = useMemo(() => {
    const counters = { nursery: 0, ready: 0, running: 0, harvest: 0 };
    for (const f of fields) {
      const p = f?.phase;
      if (p && counters[p] != null) counters[p] += 1;
    }
    return counters;
  }, [fields]);

  const activeFields = useMemo(
    () => fields.filter((f) => f.phase !== 'harvest'),
    [fields]
  );

  const closedCount = phaseCounts.harvest;
  const readyToPlantCount = phaseCounts.ready;

  const overdueCount = useMemo(
    () => fields.filter((f) => f.phase === 'running' && f.status === 'overdue').length,
    [fields]
  );

  const dueCount = useMemo(
    () => fields.filter((f) => f.phase === 'running' && f.status === 'due').length,
    [fields]
  );

  const totalCostAll = useMemo(() => {
    const sum = activeFields.reduce((acc, f) => acc + (safeNum(f.cost_total) || 0), 0);
    return sum > 0 ? sum : null;
  }, [activeFields]);

  const draftCount = drafts?.length || 0;
  const selectableFields = useMemo(() => {
    const source = Array.isArray(allFields) && allFields.length ? allFields : fields;
    const uniq = new Map();
    for (const f of source || []) {
      const id = Number(f?.id ?? f?.field_id);
      if (!Number.isFinite(id)) continue;
      if (!uniq.has(id)) uniq.set(id, { ...f, id });
    }
    return Array.from(uniq.values()).sort((a, b) =>
      String(a?.name || '').localeCompare(String(b?.name || ''), 'id')
    );
  }, [allFields, fields]);
  const filteredSelectableFields = useMemo(() => {
    const q = String(fieldPickerQuery || '').trim().toLowerCase();
    if (!q) return selectableFields;
    return selectableFields.filter((f) => {
      const name = String(f?.name || '').toLowerCase();
      const variety = String(f?.seed_variety || '').toLowerCase();
      return name.includes(q) || variety.includes(q) || String(f?.id || '').includes(q);
    });
  }, [selectableFields, fieldPickerQuery]);

  const selectedSeasonLabel = selectedSeason ? getSeasonName(selectedSeason) : 'Musim aktif';
  const selectedSeasonRange = selectedSeason
    ? getSeasonRangeLabel(selectedSeason)
    : 'Filter otomatis ke musim yang sedang berjalan';

  const filteredFields = useMemo(() => {
    let arr = [...fields];

    if (tab === 'nursery') arr = arr.filter((f) => f.phase === 'nursery');
    if (tab === 'ready') arr = arr.filter((f) => f.phase === 'ready');
    if (tab === 'running') arr = arr.filter((f) => f.phase === 'running');
    if (tab === 'harvest') arr = arr.filter((f) => f.phase === 'harvest');

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      arr = arr.filter(
        (f) => (f.name || '').toLowerCase().includes(q) || (f.seed_variety || '').toLowerCase().includes(q)
      );
    }

    if (tab === 'running') {
      const rank = { overdue: 0, due: 1, ontrack: 2 };
      return [...arr].sort((a, b) => {
        const ra = rank[a.status] ?? 3;
        const rb = rank[b.status] ?? 3;
        if (ra !== rb) return ra - rb;
        const da = a.dLeft ?? 9999;
        const db = b.dLeft ?? 9999;
        return da - db;
      });
    }

    if (tab === 'ready') {
      return [...arr].sort((a, b) => {
        const da = a.readyAt ? new Date(a.readyAt).getTime() : Infinity;
        const db = b.readyAt ? new Date(b.readyAt).getTime() : Infinity;
        if (da !== db) return da - db;
        return String(a.name || '').localeCompare(String(b.name || ''), 'id');
      });
    }

    if (tab === 'nursery') {
      return [...arr].sort((a, b) => {
        const ra = Number(a.hss_ready_days || 14) - Number(a.nurseryAge || 0);
        const rb = Number(b.hss_ready_days || 14) - Number(b.nurseryAge || 0);
        const da = Number.isFinite(ra) ? ra : 9999;
        const db = Number.isFinite(rb) ? rb : 9999;
        if (da !== db) return da - db;
        return String(a.name || '').localeCompare(String(b.name || ''), 'id');
      });
    }

    if (tab === 'harvest') {
      return [...arr].sort((a, b) => {
        const da = a.harvest_date ? new Date(a.harvest_date).getTime() : 0;
        const db = b.harvest_date ? new Date(b.harvest_date).getTime() : 0;
        if (da !== db) return db - da;
        return String(a.name || '').localeCompare(String(b.name || ''), 'id');
      });
    }

    return [...arr].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'id'));
  }, [fields, tab, query]);

  const toggleHistory = async (fid) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);

    const nextOpen = !openHistory[fid];
    setOpenHistory((m) => ({ ...m, [fid]: nextOpen }));

    if (nextOpen) {
      setHistTab((m) => ({ ...m, [fid]: m[fid] || 'events' }));
      await fetchHistory(fid);
      await fetchFieldExpenses(fid);
    }
  };

  const resetAddField = () => {
    setNfName('');
    setNfSeed('');
    setNfPlant(new Date().toISOString().slice(0, 10));
    setNfHarvestDays('100');
  };

  const onCreateField = async () => {
    if (!nfName.trim()) return notify('Validasi', 'Nama lahan wajib diisi.');
    try {
      setCreating(true);
      await axios.post(
        `${API_BASE}/operations/fields`,
        {
          name: nfName.trim(),
          seed_variety: nfSeed.trim() || null,
          plant_date: nfPlant,
          harvest_days: parseInt(nfHarvestDays || '100', 10) || 100,
          cycle_days: DEFAULT_CYCLE_DAYS,
        },
        { headers: authHdr }
      );
      setShowAddField(false);
      resetAddField();
      await fetchAll({ silent: true });
      notify('Sukses', 'Lahan berhasil ditambahkan.');
    } catch (e) {
      notify('Error', e?.response?.data?.error || e?.message || 'Gagal menambah lahan');
    } finally {
      setCreating(false);
    }
  };

  const setFieldArchived = async (field, archived) => {
    const fieldId = Number(field?.id ?? field?.field_id);
    if (!Number.isFinite(fieldId)) return;

    const run = async () => {
      try {
        setArchiveSaving((current) => ({ ...current, [fieldId]: true }));
        await axios.patch(
          `${API_BASE}/operations/fields/${fieldId}/archive`,
          { archived },
          { headers: authHdr, timeout: 15000 }
        );
        await fetchAll({ silent: true });
        notify(
          'Sukses',
          archived
            ? `${field.name} diarsipkan. Seluruh histori tetap tersimpan.`
            : `${field.name} aktif kembali dan tersedia di pilihan lahan.`
        );
      } catch (e) {
        notify('Tidak dapat memproses', e?.response?.data?.error || e?.message || 'Gagal mengubah status lahan');
      } finally {
        setArchiveSaving((current) => ({ ...current, [fieldId]: false }));
      }
    };

    if (!archived) {
      await run();
      return;
    }

    const message =
      `Arsipkan lahan "${field.name}"?\n\nLahan akan hilang dari daftar aktif, tetapi hasil panen, pengeluaran, kegiatan, dan riwayat tetap aman.`;
    if (Platform.OS === 'web') {
      const confirmed = typeof window !== 'undefined' && window.confirm ? window.confirm(message) : true;
      if (confirmed) await run();
      return;
    }

    Alert.alert('Arsipkan Lahan', message, [
      { text: 'Batal', style: 'cancel' },
      { text: 'Arsipkan', style: 'destructive', onPress: run },
    ]);
  };

  const openActivityModal = (field) => {
    setActField(field);
    setActType('semprot');
    setActSubtype('');
    setActDate(new Date().toISOString().slice(0, 10));
    setActNote('');
    setActQty('');
    setActUnit('');
    setShowActivity(true);
  };

  const saveActivity = async () => {
    if (!actField?.id) return;
    try {
      setActSaving(true);
      await axios.post(
        `${API_BASE}/operations/events`,
        {
          field_id: actField.id,
          activity_type: actType,
          activity_subtype: actSubtype?.trim() || null,
          activity_date: actDate || toYMD(new Date()),
          product_or_note: actNote?.trim() || null,
          qty: parseNum(actQty),
          qty_unit: actUnit?.trim() || null,
        },
        { headers: authHdr }
      );

      setShowActivity(false);
      await fetchAll({ silent: true });
      if (openHistory[actField.id]) {
        await fetchHistory(actField.id);
        await fetchFieldExpenses(actField.id);
      }
      notify('Sukses', 'Kegiatan disimpan.');
    } catch (e) {
      notify('Error', e?.response?.data?.error || e?.message || 'Gagal simpan kegiatan');
    } finally {
      setActSaving(false);
    }
  };

  const openNurseryModal = (field = null) => {
    setNsField(field || null);
    setFieldPickerQuery('');
    setNsDate(new Date().toISOString().slice(0, 10));
    setNsReadyDays(String(field?.hss_ready_days || 14));
    setNsSeedVariety(String(field?.seed_variety || '').trim());
    setNsNote(field?.ready_note || '');
    setShowNursery(true);
  };

  const saveNursery = async () => {
    if (!nsField?.id) return notify('Validasi', 'Pilih lahan dulu.');
    if (!String(nsSeedVariety || '').trim()) return notify('Validasi', 'Varietas wajib diisi.');
    try {
      setNsSaving(true);
      await axios.post(
        `${API_BASE}/operations/fields/${nsField.id}/nursery/start`,
        {
          nursery_date: nsDate || toYMD(new Date()),
          hss_ready_days: parseInt(nsReadyDays || '14', 10) || 14,
          seed_variety: String(nsSeedVariety || '').trim(),
          ready_note: nsNote?.trim() || null,
        },
        { headers: authHdr, timeout: 15000 }
      );
      setShowNursery(false);
      const activeSid = normalizeSeasonId(activeSeason?.season_id);
      const targetSid = activeSid || normalizeSeasonId(selectedSeasonId);
      if (activeSid && String(selectedSeasonId) !== String(activeSid)) {
        setSelectedSeasonId(activeSid);
      }
      setTab('nursery');
      await fetchAll({ silent: true, seasonIdOverride: targetSid });
      notify('Sukses', 'Tabur bibit tersimpan.');
    } catch (e) {
      notify('Error', e?.response?.data?.error || e?.message || 'Gagal simpan pembibitan');
    } finally {
      setNsSaving(false);
    }
  };

  const markReadyToPlant = async (field, ready) => {
    if (!field?.id) return;
    try {
      await axios.post(
        `${API_BASE}/operations/fields/${field.id}/nursery/ready`,
        { ready, note: field?.ready_note || null },
        { headers: authHdr, timeout: 10000 }
      );
      await fetchAll({ silent: true });
      notify('Sukses', ready ? 'Lahan ditandai siap tanam.' : 'Status siap tanam dibatalkan.');
    } catch (e) {
      notify('Error', e?.response?.data?.error || e?.message || 'Gagal update status siap tanam');
    }
  };

  const openStartCycleModal = (field = null) => {
    setScField(field || null);
    setFieldPickerQuery('');
    setScPlant(new Date().toISOString().slice(0, 10));
    setScHarvestDays(String(field?.harvest_days || 100));
    setScCycleDays(String(field?.cycle_days || DEFAULT_CYCLE_DAYS));
    setScNotes('');
    setScSeedVariety(field?.seed_variety || '');
    setShowStartCycle(true);
  };

  const pickStartCycleField = (f) => {
    setScField(f);
    setScHarvestDays(String(f?.harvest_days || 100));
    setScCycleDays(String(f?.cycle_days || DEFAULT_CYCLE_DAYS));
    setScSeedVariety(f?.seed_variety || '');
  };

  const startCycle = async () => {
    if (!scField?.id) return notify('Validasi', 'Pilih lahan dulu.');
    if (!String(scSeedVariety || '').trim()) return notify('Validasi', 'Varietas wajib diisi.');

    try {
      setScSaving(true);

      await axios.post(
        `${API_BASE}/operations/fields/${scField.id}/cycle/start`,
        {
          plant_date: scPlant || toYMD(new Date()),
          seed_variety: String(scSeedVariety || '').trim(),
          harvest_days: parseInt(scHarvestDays || '100', 10) || 100,
          cycle_days: parseInt(scCycleDays || String(DEFAULT_CYCLE_DAYS), 10) || DEFAULT_CYCLE_DAYS,
          notes: scNotes?.trim() || null,
        },
        { headers: authHdr, timeout: 15000 }
      );

      const note = [
        `Tanam - ${scField?.name || `Lahan #${scField.id}`}`,
        `Varietas: ${String(scSeedVariety).trim()}`,
        `Umur: ${parseInt(scHarvestDays || '100', 10) || 100} hari`,
        `Tanggal tanam: ${scPlant || toYMD(new Date())}`,
      ].join('\n');

      await axios.post(
        `${API_BASE}/operations/events`,
        {
          field_id: scField.id,
          activity_type: 'catatan',
          activity_date: scPlant || toYMD(new Date()),
          product_or_note: note,
          qty: null,
          qty_unit: null,
          activity_subtype: null,
        },
        { headers: authHdr, timeout: 15000 }
      );

      setShowStartCycle(false);
      const activeSid = normalizeSeasonId(activeSeason?.season_id);
      const targetSid = activeSid || normalizeSeasonId(selectedSeasonId);
      if (activeSid && String(selectedSeasonId) !== String(activeSid)) {
        setSelectedSeasonId(activeSid);
      }
      setTab('running');
      await fetchAll({ silent: true, seasonIdOverride: targetSid });

      if (openHistory[scField.id]) {
        await fetchHistory(scField.id);
        await fetchFieldExpenses(scField.id);
      }

      notify('Sukses', 'Sudah tanam disimpan. Siklus baru dimulai.');
    } catch (e) {
      notify('Error', e?.response?.data?.error || e?.message || 'Gagal mulai siklus');
    } finally {
      setScSaving(false);
    }
  };

  const closeCycle = async (field) => {
    if (!field?.id) return;

    const runClose = async () => {
      try {
        const closeResult = await axios.post(
          `${API_BASE}/operations/fields/${field.id}/cycle/close`,
          { harvest_date: toYMD(new Date()), notes: null },
          { headers: authHdr, timeout: 15000 }
        );

        setFields((prev) => prev.filter((x) => (x.id ?? x.field_id) !== field.id));
        setOpenHistory((m) => {
          const n = { ...m };
          delete n[field.id];
          return n;
        });
        setHistItems((m) => {
          const n = { ...m };
          delete n[field.id];
          return n;
        });
        setExpData((m) => {
          const n = { ...m };
          delete n[field.id];
          return n;
        });

        const closedDate = closeResult?.data?.cycle?.harvest_date;
        const automaticDate = closeResult?.data?.harvest_date_source === 'first_harvest_result';
        notify(
          'Sukses',
          automaticDate
            ? `Siklus ditutup. Tanggal panen otomatis ${fmtDateShort(closedDate)} dari hasil panen pertama.`
            : `Siklus ditutup dengan tanggal panen ${fmtDateShort(closedDate)}. Lihat analisis HST di Report.`
        );
      } catch (e) {
        notify('Error', e?.response?.data?.error || e?.message || 'Gagal tutup siklus');
      }
    };

    if (Platform.OS === 'web') {
      const ok =
        typeof window !== 'undefined' && window.confirm
          ? window.confirm(`Selesai Panen?\n\nTutup siklus parent lahan "${field.name}" berdasarkan timbangan pertama?`)
          : true;
      if (ok) await runClose();
      return;
    }

    Alert.alert('Selesai Panen', `Tutup siklus parent lahan "${field.name}" berdasarkan timbangan pertama?`, [
      { text: 'Batal', style: 'cancel' },
      { text: 'Selesai Panen', style: 'destructive', onPress: runClose },
    ]);
  };

  const openPhotoModal = (field) => {
    setPhField(field);
    setPhDate(new Date().toISOString().slice(0, 10));
    setPhNote('');
    setPhAssets([]);
    setShowPhoto(true);
  };

  const pickPhotos = async () => {
    try {
      const remaining = MAX_OPERATION_PHOTOS - phAssets.length;
      if (remaining <= 0) {
        return notify('Batas Foto', 'Maksimal 8 foto dalam satu kali simpan.');
      }

      const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!perm.granted) return notify('Izin', 'Butuh izin akses galeri.');

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 0.8,
        allowsMultipleSelection: true,
        selectionLimit: remaining,
        orderedSelection: true,
      });
      if (result.canceled) return;

      const picked = (Array.isArray(result.assets) ? result.assets : [])
        .filter((asset) => asset?.uri)
        .slice(0, remaining);

      setPhAssets((current) => {
        const seen = new Set(current.map((asset) => asset.assetId || asset.uri));
        const next = [...current];
        for (const asset of picked) {
          const key = asset.assetId || asset.uri;
          if (!seen.has(key)) {
            seen.add(key);
            next.push(asset);
          }
        }
        return next.slice(0, MAX_OPERATION_PHOTOS);
      });
    } catch (e) {
      notify('Error', e?.message || 'Gagal memilih foto');
    }
  };

  const removePickedPhoto = (index) => {
    if (phUploading) return;
    setPhAssets((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const openImagePreview = (uris, index = 0, title = 'Dokumentasi Lahan', subtitle = '') => {
    const safeUris = (Array.isArray(uris) ? uris : [uris]).filter(Boolean);
    if (!safeUris.length) return;
    const safeIndex = Math.max(0, Math.min(Number(index) || 0, safeUris.length - 1));
    setImgPreview({ open: true, uris: safeUris, index: safeIndex, title, subtitle });
  };

  const closeImagePreview = () => {
    setImgPreview({ open: false, uris: [], index: 0, title: '', subtitle: '' });
  };

  const moveImagePreview = (delta) => {
    setImgPreview((current) => {
      const total = current.uris?.length || 0;
      if (total <= 1) return current;
      const nextIndex = (current.index + delta + total) % total;
      return { ...current, index: nextIndex };
    });
  };

  const uploadPhotos = async () => {
    if (!phField?.id) return;
    if (!phAssets.length) return notify('Validasi', 'Pilih minimal satu foto.');

    try {
      setPhUploading(true);

      const form = new FormData();
      form.append('date', phDate || toYMD(new Date()));
      form.append('note', phNote?.trim() || '');

      for (let index = 0; index < phAssets.length; index += 1) {
        const asset = phAssets[index];
        if (Platform.OS === 'web') {
          const response = await fetch(asset.uri);
          const blob = await response.blob();
          const fileName = asset.fileName || 'foto-lahan-' + Date.now() + '-' + index + '.jpg';
          const fileType = blob.type || asset.mimeType || 'image/jpeg';
          form.append('photos', new File([blob], fileName, { type: fileType }));
        } else {
          form.append('photos', {
            uri: asset.uri,
            name: asset.fileName || 'foto-lahan-' + Date.now() + '-' + index + '.jpg',
            type: asset.mimeType || 'image/jpeg',
          });
        }
      }

      const response = await axios.post(`${API_BASE}/operations/fields/${phField.id}/photo`, form, {
        headers: { ...authHdr },
        timeout: 120000,
      });

      const savedCount = Number(response.data?.photo_count || phAssets.length);
      setShowPhoto(false);
      setPhAssets([]);
      await fetchAll({ silent: true });
      if (openHistory[phField.id]) {
        await fetchHistory(phField.id);
        await fetchFieldExpenses(phField.id);
      }

      notify('Foto Tersimpan', savedCount + ' foto berhasil masuk ke timeline ' + phField.name + '.');
    } catch (e) {
      notify('Error', e?.response?.data?.error || e?.message || 'Gagal upload foto');
    } finally {
      setPhUploading(false);
    }
  };

  const resetExpenseForm = () => {
    setExDate(new Date().toISOString().slice(0, 10));
    setExCategory('Pupuk');
    setExSubcategory('');
    setExItem('');
    setExQty('');
    setExUnit('');
    setExUnitPrice('');
    setExTotal('');
    setExNotes('');
  };

  const computeTotalLive = (qtyStr, unitPriceStr, totalStr) => {
    const t = parseNum(totalStr);
    if (t != null) return t;
    const q = parseNum(qtyStr);
    const p = parseNum(unitPriceStr);
    if (q != null && p != null) return q * p;
    return null;
  };

  const openAddExpenseModal = (field) => {
    setExField(field);
    resetExpenseForm();
    setShowAddExpense(true);
  };

  const saveFieldExpense = async () => {
    if (!exField?.id) return;

    if (!String(exCategory || '').trim()) return notify('Validasi', 'Kategori wajib diisi.');
    if (!String(exItem || '').trim()) return notify('Validasi', 'Nama item wajib diisi.');

    const finalTotal = computeTotalLive(exQty, exUnitPrice, exTotal);
    if (finalTotal == null || !Number.isFinite(finalTotal) || finalTotal <= 0) {
      return notify('Validasi', 'Total wajib valid. Isi total atau isi qty + harga.');
    }

    try {
      setExSaving(true);

      await axios.post(
        `${API_BASE}/operations/fields/${exField.id}/expenses`,
        {
          expense_date: exDate || new Date().toISOString().slice(0, 10),
          category: String(exCategory).trim(),
          subcategory: exSubcategory ? String(exSubcategory).trim() : null,
          item_name: String(exItem).trim(),
          qty: parseNum(exQty),
          unit: exUnit ? String(exUnit).trim() : null,
          unit_price: parseNum(exUnitPrice),
          total: finalTotal,
          notes: exNotes ? String(exNotes).trim() : null,
        },
        { headers: authHdr, timeout: 15000 }
      );

      await fetchAll({ silent: true });
      await fetchFieldExpenses(exField.id);

      resetExpenseForm();
      notify('Sukses', 'Pengeluaran tersimpan.');
    } catch (e) {
      notify('Error', e?.response?.data?.error || e?.message || 'Gagal simpan pengeluaran');
    } finally {
      setExSaving(false);
    }
  };

  const setDraftLocal = (id, patch) =>
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));

  const confirmDraft = async (d) => {
    if (!d.field_id) return notify('Validasi', 'Assign lahan dulu.');
    try {
      setDraftSaving((m) => ({ ...m, [d.id]: true }));
      await axios.post(
        `${API_BASE}/operations/spray-logs/${d.id}/confirm`,
        {
          field_id: d.field_id,
          date: d.date || toYMD(new Date()),
          dose_amount: d.dose_amount ? Number(d.dose_amount) : null,
          dose_unit: d.dose_unit || null,
          volume_l: d.volume_l ? Number(d.volume_l) : null,
        },
        { headers: authHdr }
      );

      await fetchAll({ silent: true });
      if (openHistory[d.field_id]) {
        await fetchHistory(d.field_id);
        await fetchFieldExpenses(d.field_id);
      }
      notify('Sukses', 'Draft dikonfirmasi.');
    } catch (e) {
      notify('Error', e?.response?.data?.error || e?.message || 'Gagal konfirmasi');
    } finally {
      setDraftSaving((m) => ({ ...m, [d.id]: false }));
    }
  };


  const reloadRiceNotes = async () => {
    const response = await axios.get(`${API_BASE}/operations/rice-notes`, { headers: authHdr });
    const items = Array.isArray(response?.data?.items) ? response.data.items : [];
    setRiceNotes(items);
    return items;
  };

  const openRiceNoteModal = () => {
    setRnTitle('');
    setRnBody('');
    setRnVariety('');
    setRnPhase('anytime');
    setRnHstFrom('0');
    setRnHstTo('30');
    setRnPriority('important');
    setRnFieldId(null);
    setShowAddRiceNote(true);
  };

  const saveRiceNote = async () => {
    if (!rnTitle.trim()) return notify('Validasi', 'Judul catatan wajib diisi.');
    if (!rnBody.trim()) return notify('Validasi', 'Isi catatan wajib diisi.');
    if (rnPhase === 'hst') {
      const from = Number(rnHstFrom);
      const to = Number(rnHstTo);
      if (!Number.isInteger(from) || from < 0 || !Number.isInteger(to) || to < from) {
        return notify('Validasi', 'Rentang HST belum benar.');
      }
    }

    try {
      setRnSaving(true);
      await axios.post(
        `${API_BASE}/operations/rice-notes`,
        {
          title: rnTitle.trim(),
          body: rnBody.trim(),
          variety: rnVariety.trim() || null,
          trigger_phase: rnPhase,
          hst_from: rnPhase === 'hst' ? Number(rnHstFrom) : null,
          hst_to: rnPhase === 'hst' ? Number(rnHstTo) : null,
          priority: rnPriority,
          field_id: rnFieldId,
        },
        { headers: authHdr, timeout: 15000 }
      );
      await reloadRiceNotes();
      setShowAddRiceNote(false);
      setRiceNotesExpanded(true);
      notify('Sukses', 'Catatan Padi tersimpan dan akan muncul pada waktu yang sesuai.');
    } catch (e) {
      notify('Error', e?.response?.data?.error || e?.message || 'Gagal menyimpan Catatan Padi');
    } finally {
      setRnSaving(false);
    }
  };

  const archiveRiceNote = async (note) => {
    const runArchive = async () => {
      try {
        await axios.patch(
          `${API_BASE}/operations/rice-notes/${note.note_id}/archive`,
          {},
          { headers: authHdr, timeout: 10000 }
        );
        setRiceNotes((prev) => prev.filter((item) => item.note_id !== note.note_id));
      } catch (e) {
        notify('Error', e?.response?.data?.error || e?.message || 'Gagal mengarsipkan catatan');
      }
    };

    if (Platform.OS === 'web') {
      const ok = typeof window === 'undefined' || !window.confirm || window.confirm(`Arsipkan catatan “${note.title}”?`);
      if (ok) await runArchive();
      return;
    }
    Alert.alert('Arsipkan Catatan', `Catatan “${note.title}” tidak akan ditampilkan lagi.`, [
      { text: 'Batal', style: 'cancel' },
      { text: 'Arsipkan', style: 'destructive', onPress: runArchive },
    ]);
  };

  const getRiceNotesForContext = (variety, phase, hst = null, fieldId = null) => {
    const varietyKey = normalizeVarietyKey(variety);
    return riceNotes.filter((note) => {
      if (note.field_id != null && Number(note.field_id) !== Number(fieldId)) return false;
      if (note.variety && normalizeVarietyKey(note.variety) !== varietyKey) return false;
      if (note.trigger_phase === 'anytime') return true;
      if (note.trigger_phase !== phase) return false;
      if (phase !== 'hst') return true;
      const age = Number(hst);
      return Number.isFinite(age) && age >= Number(note.hst_from || 0) && age <= Number(note.hst_to || 0);
    });
  };

  const RiceReminderList = ({ items }) => {
    if (!items?.length) return null;
    return (
      <View style={s.riceReminderBox}>
        <View style={s.riceReminderHead}>
          <PadiProIcon name="catatan" size={14} color={T.greenDark} strokeWidth={2} />
          <Text style={s.riceReminderHeadText}>Pengingat Catatan Padi</Text>
        </View>
        {items.slice(0, 3).map((note) => (
          <View key={note.note_id} style={s.riceReminderItem}>
            <Text style={s.riceReminderTitle}>{note.title}</Text>
            <Text style={s.riceReminderBody}>{note.body}</Text>
          </View>
        ))}
      </View>
    );
  };
  const StatusBadge = ({ status }) => {
    const map = {
      overdue: { text: 'Terlambat', bg: T.dangerSoft, bd: T.danger, fg: T.danger },
      due: { text: 'H-2', bg: T.warnSoft, bd: T.warn, fg: T.warn },
      ontrack: { text: 'Aman', bg: T.greenSoft, bd: T.greenSoft2, fg: T.greenDark },
    };
    const m = map[status] || map.ontrack;
    return (
      <View style={[s.badge, { backgroundColor: m.bg, borderColor: m.bd }]}>
        <Text style={[s.badgeText, { color: m.fg }]}>{m.text}</Text>
      </View>
    );
  };

  const TimelineRow = ({ type, title, subtitle, lines = [], count = 1, isLast = false, photoUris = [] }) => {
    const st = ActivityStyle(type);
    const photos = Array.from(new Set((Array.isArray(photoUris) ? photoUris : []).filter(Boolean)));
    const galleryTitle = title === 'FOTO' ? 'Perkembangan Padi' : title;

    return (
      <View style={s.tlRow}>
        <View style={s.tlRail}>
          <View style={[s.tlDot, { backgroundColor: st.dot }]} />
          {!isLast && <View style={s.tlLine} />}
        </View>

        <View style={[s.tlCard, { backgroundColor: st.bg, borderColor: st.border }]}>
          <View style={s.tlHead}>
            <View style={s.tlHeadLeft}>
              <View style={s.tlIconWrap}>
                <PadiProIcon name={st.glyph} size={16} color={st.dot} strokeWidth={1.9} />
              </View>

              <View style={{ flex: 1 }}>
                <Text style={s.tlTitle}>{galleryTitle}</Text>
                {!!subtitle && <Text style={s.tlSub}>{subtitle}</Text>}
              </View>
            </View>

            {(photos.length > 0 || count > 1) && (
              <View style={s.tlCountPill}>
                <Text style={s.tlCountText}>{photos.length > 0 ? photos.length + ' foto' : count + ' item'}</Text>
              </View>
            )}
          </View>

          {!!lines?.length && (
            <View style={s.tlLinesWrap}>
              {lines.map((line, idx) => (
                <View key={title + '-' + idx} style={s.tlBulletRow}>
                  <View style={s.tlBulletDot} />
                  <Text style={s.tlBulletText}>{line}</Text>
                </View>
              ))}
            </View>
          )}

          {photos.length === 1 && (
            <TouchableOpacity
              style={s.photoWrap}
              onPress={() => openImagePreview(photos, 0, galleryTitle, subtitle)}
              activeOpacity={0.88}
            >
              <Image
                source={{ uri: photos[0] }}
                style={s.photoImg}
                resizeMode="cover"
                onError={(e) => console.log('[history photo] load error:', photos[0], e?.nativeEvent)}
              />
              <View style={s.photoOpenBar}>
                <Text style={s.photoHint}>Lihat foto penuh</Text>
                <Text style={s.photoOpenArrow}>›</Text>
              </View>
            </TouchableOpacity>
          )}

          {photos.length > 1 && (
            <View style={s.photoGalleryBlock}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={s.photoGalleryRail}
              >
                {photos.map((uri, photoIndex) => (
                  <TouchableOpacity
                    key={uri}
                    style={s.photoThumbButton}
                    onPress={() => openImagePreview(photos, photoIndex, galleryTitle, subtitle)}
                    activeOpacity={0.88}
                  >
                    <Image
                      source={{ uri }}
                      style={s.photoThumb}
                      resizeMode="cover"
                      onError={(e) => console.log('[history photo] load error:', uri, e?.nativeEvent)}
                    />
                    <View style={s.photoIndexBadge}>
                      <Text style={s.photoIndexText}>{photoIndex + 1}</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              <Text style={s.photoGalleryHint}>Ketuk foto untuk melihat dan membandingkan kondisi lahan.</Text>
            </View>
          )}
        </View>
      </View>
    );
  };

  const FieldCard = ({ f }) => {
    const isOpen = !!openHistory[f.id];
    const activeHistTab = histTab[f.id] || 'events';
    const phase = f.phase || 'nursery';
    const isRunning = phase === 'running';
    const isReady = phase === 'ready';
    const isNursery = phase === 'nursery';
    const isHarvest = phase === 'harvest';

    const costTotal = safeNum(f.cost_total);
    const costPerRante = safeNum(f.cost_per_rante);
    const readyDays = Number(f.hss_ready_days || 14);
    const nurseryAge = Number.isFinite(Number(f.nurseryAge)) ? Number(f.nurseryAge) : null;
    const hssLeft = nurseryAge != null ? Math.max(0, readyDays - nurseryAge) : null;

    const exp = expData[f.id] || { total: 0, items: [] };
    const expTotal = safeNum(exp.total) ?? 0;
    const expItems = Array.isArray(exp.items) ? exp.items : [];
    const costCategory = summarizeCostCategory(f, expItems);
    const costCategoryLabel = costCategory || 'Belum terpetakan';

    const groupedEvents = groupHistoryItems(histItems[f.id] || [], f.plant_date).slice(0, 60);

    return (
      <View style={s.card}>
        <View style={s.cardTop}>
          <View style={{ flex: 1 }}>
            <Text style={s.cardTitle}>{f.name || `Lahan #${f.id}`}</Text>
            <Text style={s.cardSub}>
              Varietas: <Text style={s.cardSubStrong}>{f.seed_variety || '—'}</Text>
            </Text>
          </View>
          {isRunning ? (
            <StatusBadge status={f.status} />
          ) : (
            <View style={[s.badge, { backgroundColor: isReady ? T.warnSoft : isHarvest ? PADIPRO.surfaceSunken : T.greenSoft, borderColor: isReady ? T.warn : T.border }]}>
              <Text style={[s.badgeText, { color: isReady ? T.warn : isHarvest ? PADIPRO.muted : T.greenDark }]}>
                {isReady ? 'Siap Tanam' : isHarvest ? 'Panen' : 'Pembibitan'}
              </Text>
            </View>
          )}
        </View>

        {isRunning ? (
          <ProgressHarvest
            plantAge={f.plantAge ?? 0}
            targetDays={f.harvest_days || 100}
            plantDate={f.plant_date}
            harvestAt={f.harvestAt}
          />
        ) : (
          <View style={s.nurseryBox}>
            <Text style={s.nurseryTitle}>{isReady ? 'Bibit Siap Tanam' : isHarvest ? 'Siklus Sudah Ditutup' : 'Fase Pembibitan'}</Text>
            <Text style={s.nurseryMeta}>
              Tabur: <Text style={s.nurseryStrong}>{fmtDateShort(f.nursery_date)}</Text>
              {nurseryAge != null ? ` · HSS ${nurseryAge}` : ' · HSS -'}
            </Text>
            <Text style={s.nurseryMeta}>
              Target siap: <Text style={s.nurseryStrong}>{fmtDateShort(f.readyAt)}</Text>
              {hssLeft != null ? ` · ${hssLeft}h lagi` : ''}
            </Text>
          </View>
        )}

        {isRunning && (
          <RiceReminderList
            items={getRiceNotesForContext(f.seed_variety, 'hst', f.plantAge, f.id)}
          />
        )}

        {isRunning && !!f.first_harvest_date && (
          <View style={s.harvestAutoBox}>
            <View style={s.harvestAutoHead}>
              <Text style={s.harvestAutoTitle}>Panen berlangsung · Parent lahan</Text>
              {Number.isFinite(Number(f.actual_hst)) && (
                <View style={s.harvestAutoHstBadge}>
                  <Text style={s.harvestAutoHstText}>{Number(f.actual_hst)} HST</Text>
                </View>
              )}
            </View>
            <Text style={s.harvestAutoMeta}>
              Timbangan pertama: <Text style={s.harvestAutoStrong}>{fmtDateShort(f.first_harvest_date)}</Text>
              {f.last_harvest_date && String(f.last_harvest_date).slice(0,10) !== String(f.first_harvest_date).slice(0,10)
                ? ` · Terakhir ${fmtDateShort(f.last_harvest_date)}`
                : ''}
            </Text>
            <Text style={s.harvestAutoMeta}>
              {Number(f.harvest_record_count || 0)} catatan · {Number(f.harvest_total_kg || 0).toLocaleString('id-ID')} kg, termasuk semua subpetak
            </Text>
          </View>
        )}


        <View style={s.fieldStats}>
          <View style={s.fieldStatItem}>
            <Text style={s.fieldStatLabel}>LUAS</Text>
            <Text style={s.fieldStatValue} numberOfLines={1}>{f.rante ? String(f.rante) + ' rante' : '—'}</Text>
          </View>
          <View style={s.fieldStatDivider} />
          <View style={s.fieldStatItem}>
            <Text style={s.fieldStatLabel}>{isRunning ? 'SEMPROT BERIKUT' : isHarvest ? 'HASIL PANEN' : 'TARGET SIAP'}</Text>
            <Text style={s.fieldStatValue} numberOfLines={1} adjustsFontSizeToFit>
              {isRunning
                ? fmtDateShort(f.dueDate)
                : isHarvest
                  ? Number(f.harvest_total_kg || 0).toLocaleString('id-ID') + ' kg'
                  : fmtDateShort(f.readyAt)}
            </Text>
          </View>
          <View style={s.fieldStatDivider} />
          <View style={s.fieldStatItem}>
            <Text style={s.fieldStatLabel}>BIAYA</Text>
            <Text style={s.fieldStatValue} numberOfLines={1} adjustsFontSizeToFit>
              {formatIDR(expTotal || costTotal || 0)}
            </Text>
          </View>
        </View>
        <Text style={s.fieldCostHint} numberOfLines={1}>
          {costCategoryLabel}{costPerRante != null ? ' · ' + formatIDR(costPerRante) + '/rante' : ''}
        </Text>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={s.cardActionRail}
        >
          {isNursery && (
            <>
              <TouchableOpacity style={[s.cardActionBtn, s.cardActionPrimary]} onPress={() => openNurseryModal(f)}>
                <Text style={s.btnTextWhite}>Catat Tabur</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[s.cardActionBtn, s.cardActionWarn]} onPress={() => markReadyToPlant(f, true)}>
                <Text style={s.btnTextDark}>Tandai Siap</Text>
              </TouchableOpacity>
            </>
          )}
          {isReady && (
            <>
              <TouchableOpacity style={[s.cardActionBtn, s.cardActionPrimary]} onPress={() => openStartCycleModal(f)}>
                <Text style={s.btnTextWhite}>Mulai Tanam</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[s.cardActionBtn, s.cardActionGhost]} onPress={() => markReadyToPlant(f, false)}>
                <Text style={s.btnTextDark}>Belum Siap</Text>
              </TouchableOpacity>
            </>
          )}
          {isRunning && (
            <>
              <TouchableOpacity style={[s.cardActionBtn, s.cardActionPrimary]} onPress={() => openActivityModal(f)}>
                <Text style={s.btnTextWhite}>+ Kegiatan</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[s.cardActionBtn, s.cardActionGhost]} onPress={() => openPhotoModal(f)}>
                <Text style={s.btnTextDark}>Foto</Text>
              </TouchableOpacity>
            </>
          )}
          {isHarvest && (
            <TouchableOpacity style={[s.cardActionBtn, s.cardActionPrimary]} onPress={() => navigation?.navigate?.('Report', { token, user })}>
              <Text style={s.btnTextWhite}>Lihat Report</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity style={[s.cardActionBtn, s.cardActionGhost]} onPress={() => openAddExpenseModal(f)}>
            <Text style={s.btnTextDark}>+ Biaya</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[s.cardActionBtn, s.cardActionGhost]} onPress={() => toggleHistory(f.id)} activeOpacity={0.85}>
            <Text style={s.btnTextDark}>{isOpen ? 'Tutup Detail' : 'Lihat Detail'}</Text>
          </TouchableOpacity>
          {isRunning && (
            <TouchableOpacity style={[s.cardActionBtn, s.cardActionDanger]} onPress={() => closeCycle(f)}>
              <Text style={s.btnTextWhite}>Selesai Panen</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={[s.cardActionBtn, s.cardActionArchive, archiveSaving[f.id] && { opacity: .55 }]}
            disabled={!!archiveSaving[f.id]}
            onPress={() => setFieldArchived(f, true)}
          >
            <Text style={s.btnArchiveText}>{archiveSaving[f.id] ? 'Memproses...' : 'Arsipkan'}</Text>
          </TouchableOpacity>
        </ScrollView>

        {isOpen && (
          <View style={{ marginTop: 12 }}>
            <View style={s.segment}>
              <TouchableOpacity
                style={[s.segBtn, activeHistTab === 'events' && s.segBtnActive]}
                onPress={() => setHistTab((m) => ({ ...m, [f.id]: 'events' }))}
              >
                <Text style={[s.segText, activeHistTab === 'events' && s.segTextActive]}>Aktivitas</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[s.segBtn, activeHistTab === 'cost' && s.segBtnActive]}
                onPress={async () => {
                  setHistTab((m) => ({ ...m, [f.id]: 'cost' }));
                  if (!expData[f.id]) await fetchFieldExpenses(f.id);
                }}
              >
                <Text style={[s.segText, activeHistTab === 'cost' && s.segTextActive]}>
                  Biaya {expTotal > 0 ? `(${formatIDR(expTotal)})` : ''}
                </Text>
              </TouchableOpacity>
            </View>

            {activeHistTab === 'events' ? (
              histLoading[f.id] ? (
                <View style={{ paddingVertical: 10 }}>
                  <ActivityIndicator />
                </View>
              ) : groupedEvents.length === 0 ? (
                <Text style={s.emptyInline}>Belum ada aktivitas.</Text>
              ) : (
                <View style={{ marginTop: 4 }}>
                  {groupedEvents.map((grp, idx, arr) => {
                    const isLast = idx === arr.length - 1;

                    return (
                      <TimelineRow
                        key={grp._groupKey}
                        type={grp.type}
                        title={grp.title}
                        subtitle={[grp.dateLabel, grp.ageLabel].filter(Boolean).join(' · ')}
                        lines={grp.lines}
                        count={grp.count}
                        isLast={isLast}
                        photoUris={grp.photoUris}
                      />
                    );
                  })}
                </View>
              )
            ) : (
              <View style={{ marginTop: 6 }}>
                <View style={s.costHeaderRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={s.costHeaderTitle}>Biaya detail ({selectedSeasonLabel})</Text>
                    <Text style={s.costHeaderSub}>
                      Total: <Text style={s.costHeaderStrong}>{formatIDR(expTotal)}</Text>
                    </Text>
                  </View>

                  <TouchableOpacity style={s.smallBtn} onPress={() => openAddExpenseModal(f)} activeOpacity={0.85}>
                    <Text style={s.smallBtnText}>+ Tambah</Text>
                  </TouchableOpacity>
                </View>

                {expLoading[f.id] ? (
                  <View style={{ paddingVertical: 10 }}>
                    <ActivityIndicator />
                  </View>
                ) : expItems.length === 0 ? (
                  <View style={s.emptyMini}>
                    <Text style={s.emptyMiniTitle}>Belum ada biaya</Text>
                    <Text style={s.emptyMiniSub}>Tambah pupuk, pestisida, tenaga kerja, sewa, dll.</Text>
                  </View>
                ) : (
                  <View style={{ gap: 8, marginTop: 8 }}>
                    {expItems.slice(0, 30).map((it) => (
                      <View key={it.field_expense_id} style={s.expItem}>
                        <View style={{ flexDirection: 'row', gap: 10 }}>
                          <View style={{ flex: 1 }}>
                            <Text style={s.expTitle} numberOfLines={1}>
                              {it.item_name}{' '}
                              {!!it.subcategory && <Text style={s.expSubcat}>· {it.subcategory}</Text>}
                            </Text>
                            <Text style={s.expMeta}>
                              {fmtDateShort(it.expense_date)} · {it.category}
                              {it.qty != null ? ` · ${it.qty}${it.unit ? ` ${it.unit}` : ''}` : ''}
                              {it.unit_price != null ? ` · @${formatIDR(it.unit_price)}` : ''}
                            </Text>
                            {!!it.notes && (
                              <Text style={s.expNotes} numberOfLines={2}>
                                {it.notes}
                              </Text>
                            )}
                          </View>

                          <View style={s.expAmountPill}>
                            <Text style={s.expAmountText}>{formatIDR(it.total)}</Text>
                          </View>
                        </View>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            )}
          </View>
        )}
      </View>
    );
  };

  const DraftCard = ({ d }) => {
    const saving = !!draftSaving[d.id];
    return (
      <View style={s.draftCard}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 10 }}>
          <View style={{ flex: 1 }}>
            <Text style={s.draftTitle}>Draft Scan</Text>
            <Text style={s.draftSub}>
              {d.pesticide_name || d.item_name || 'Pestisida'} · {d.qty_used ?? d.quantity ?? '—'}{' '}
              {d.dose_unit || d.unit || ''}
            </Text>
          </View>
          <View style={[s.badge, { backgroundColor: T.greenSoft, borderColor: T.greenSoft2 }]}>
            <Text style={[s.badgeText, { color: T.greenDark }]}>draft</Text>
          </View>
        </View>

        <Text style={s.label}>Assign lahan</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.chipRow}>
          {(selectableFields || []).map((f) => (
            <TouchableOpacity
              key={f.id}
              style={[s.chip, d.field_id === f.id && s.chipActive]}
              onPress={() => setDraftLocal(d.id, { field_id: f.id })}
            >
              <Text style={[s.chipText, d.field_id === f.id && s.chipTextActive]}>{f.name || `#${f.id}`}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        <TouchableOpacity
          style={[s.btn, s.btnPrimary, { marginTop: 10, opacity: saving ? 0.7 : 1 }]}
          disabled={saving}
          onPress={() => confirmDraft(d)}
        >
          <Text style={s.btnTextWhite}>{saving ? 'Menyimpan...' : 'Confirm'}</Text>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View style={{ flex: 1, backgroundColor: T.bg }}>
      <View style={[s.header, contentWidthStyle, { paddingHorizontal: contentPadding }]}>
        <EnterpriseHeader
          title="Operasional"
          subtitle={selectedSeasonLabel + ' · ' + phaseCounts.running + ' berjalan · ' + readyToPlantCount + ' siap tanam'}
          right={(
            <TouchableOpacity style={s.headerIconBtn} onPress={onRefresh} activeOpacity={0.85}>
              <PadiProIcon name="operations" size={16} color="#f4fff8" strokeWidth={2} />
            </TouchableOpacity>
          )}
        />
        {closedCount > 0 ? <Text style={s.hHint}>Lahan yang sudah panen pindah ke Report.</Text> : null}
      </View>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={[
          s.scrollContent,
          contentWidthStyle,
          { paddingHorizontal: contentPadding, paddingBottom: 24 + bottomInset },
        ]}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={T.green} />}
        contentInsetAdjustmentBehavior="automatic"
        showsVerticalScrollIndicator={false}
      >

        <View style={s.overviewCard}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={s.overviewEyebrow}>FOKUS MUSIM</Text>
            <Text style={s.overviewTitle} numberOfLines={1}>{selectedSeasonLabel}</Text>
            <Text style={s.overviewMeta}>
              {phaseCounts.running} berjalan · {phaseCounts.nursery + phaseCounts.ready} persiapan
              {draftCount > 0 ? ' · ' + draftCount + ' draft' : ''} · Modal {formatIDR(totalCostAll || 0)}
            </Text>
          </View>
          <TouchableOpacity
            style={[s.overviewAttention, overdueCount + dueCount > 0 && s.overviewAttentionWarn]}
            onPress={() => setTab('running')}
            activeOpacity={0.85}
          >
            <Text style={s.overviewAttentionValue}>{overdueCount + dueCount}</Text>
            <Text style={s.overviewAttentionLabel}>Butuh aksi</Text>
          </TouchableOpacity>
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={s.quickActionRail}
          style={s.quickActionScroll}
        >
          <TouchableOpacity style={[s.quickAction, s.quickActionPrimary]} onPress={() => openNurseryModal(null)} activeOpacity={0.88}>
            <View style={[s.quickActionIcon, s.quickActionIconPrimary]}>
              <PadiProIcon name="monitoring" size={15} color="#fff" strokeWidth={2} />
            </View>
            <Text style={[s.quickActionText, s.quickActionTextPrimary]}>Tabur Bibit</Text>
          </TouchableOpacity>
          <TouchableOpacity style={s.quickAction} onPress={openStartCycleModal} activeOpacity={0.88}>
            <View style={s.quickActionIcon}>
              <PadiProIcon name="harvest" size={15} color={T.greenDark} strokeWidth={2} />
            </View>
            <Text style={s.quickActionText}>Sudah Tanam</Text>
          </TouchableOpacity>
          <TouchableOpacity style={s.quickAction} onPress={() => setShowAddField(true)} activeOpacity={0.88}>
            <View style={s.quickActionIcon}>
              <PadiProIcon name="inventory" size={15} color={T.greenDark} strokeWidth={2} />
            </View>
            <Text style={s.quickActionText}>Tambah Lahan</Text>
          </TouchableOpacity>
          <TouchableOpacity style={s.quickAction} onPress={() => navigation?.navigate?.('Report', { token, user })} activeOpacity={0.88}>
            <View style={s.quickActionIcon}>
              <PadiProIcon name="report" size={15} color={T.greenDark} strokeWidth={2} />
            </View>
            <Text style={s.quickActionText}>Report</Text>
          </TouchableOpacity>
          <TouchableOpacity style={s.quickAction} onPress={() => setShowArchivedFields(true)} activeOpacity={0.88}>
            <View style={s.quickActionIcon}>
              <PadiProIcon name="archive" size={15} color={T.greenDark} strokeWidth={2} />
            </View>
            <Text style={s.quickActionText}>Arsip ({archivedFields.length})</Text>
          </TouchableOpacity>
        </ScrollView>

        <View style={s.riceNotesCard}>
          <View style={s.riceNotesHead}>
            <View style={s.riceNotesTitleWrap}>
              <View style={s.riceNotesIcon}>
                <PadiProIcon name="catatan" size={16} color={T.greenDark} strokeWidth={2} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={s.riceNotesTitle}>Catatan Padi</Text>
                <Text style={s.riceNotesSub}>{riceNotes.length} catatan penting lintas musim</Text>
              </View>
            </View>
            <View style={s.riceNotesHeadActions}>
              {riceNotes.length > 0 && (
                <TouchableOpacity style={s.riceNotesViewBtn} onPress={() => setRiceNotesExpanded((v) => !v)} activeOpacity={0.85}>
                  <Text style={s.riceNotesViewText}>{riceNotesExpanded ? 'Ringkas' : 'Lihat'}</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={s.riceNotesAddBtn} onPress={openRiceNoteModal} activeOpacity={0.85}>
                <Text style={s.riceNotesAddText}>+ Tulis</Text>
              </TouchableOpacity>
            </View>
          </View>

          {riceNotes.length === 0 ? (
            <Text style={s.riceNotesEmpty}>Belum ada catatan. Tulis pengingat perawatan yang penting.</Text>
          ) : riceNotesExpanded ? (
            <View style={s.riceNotesList}>
              {riceNotes.map((note) => (
                <View
                  key={note.note_id}
                  style={[s.riceNoteMini, note.priority === 'urgent' && s.riceNoteMiniUrgent]}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={s.riceNoteMiniTitle}>{note.title}</Text>
                    <Text style={s.riceNoteMiniMeta}>
                      {note.variety || 'Semua varietas'} · {riceNotePhaseLabel(note)}
                      {note.field_name ? ' · ' + note.field_name : ''}
                    </Text>
                    <Text style={s.riceNoteMiniBody} numberOfLines={4}>{note.body}</Text>
                  </View>
                  <TouchableOpacity style={s.riceNoteArchiveBtn} onPress={() => archiveRiceNote(note)}>
                    <Text style={s.riceNoteArchiveText}>Arsip</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          ) : (
            <TouchableOpacity style={s.riceNotePreview} onPress={() => setRiceNotesExpanded(true)} activeOpacity={0.82}>
              <View style={[s.riceNotePreviewDot, riceNotes[0]?.priority === 'urgent' && s.riceNotePreviewDotUrgent]} />
              <Text style={s.riceNotePreviewText} numberOfLines={1}>
                {riceNotes[0]?.title} · {riceNotes[0]?.body}
              </Text>
              <Text style={s.riceNotePreviewArrow}>›</Text>
            </TouchableOpacity>
          )}
        </View>

        <View style={s.toolbar}>
          <View style={s.searchBox}>
            <View style={s.searchIcon}>
              <PadiProIcon name="default" size={13} color={T.muted} strokeWidth={2} />
            </View>
            <TextInput
              value={query}
              onChangeText={setQuery}
              placeholder="Cari lahan atau varietas..."
              placeholderTextColor={T.muted}
              style={{ flex: 1, color: T.text, fontWeight: '700' }}
            />
            {!!query && (
              <TouchableOpacity onPress={() => setQuery('')} style={s.searchClearBtn} activeOpacity={0.85}>
                <Text style={s.searchClearText}>Reset</Text>
              </TouchableOpacity>
            )}
          </View>

          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.compactTabs}>
            <TouchableOpacity style={[s.tabBtn, tab === 'nursery' && s.tabBtnActive]} onPress={() => setTab('nursery')}>
              <Text style={[s.tabText, tab === 'nursery' && s.tabTextActive]}>Pembibitan · {phaseCounts.nursery}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.tabBtn, tab === 'ready' && s.tabBtnActive]} onPress={() => setTab('ready')}>
              <Text style={[s.tabText, tab === 'ready' && s.tabTextActive]}>Siap Tanam · {phaseCounts.ready}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.tabBtn, tab === 'running' && s.tabBtnActive]} onPress={() => setTab('running')}>
              <Text style={[s.tabText, tab === 'running' && s.tabTextActive]}>Berjalan · {phaseCounts.running}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.tabBtn, tab === 'harvest' && s.tabBtnActive]} onPress={() => setTab('harvest')}>
              <Text style={[s.tabText, tab === 'harvest' && s.tabTextActive]}>Panen · {phaseCounts.harvest}</Text>
            </TouchableOpacity>
          </ScrollView>

          <TouchableOpacity
            style={[s.seasonCompact, showSeasonFilter && s.seasonCompactActive]}
            onPress={() => setShowSeasonFilter((v) => !v)}
            activeOpacity={0.85}
          >
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={s.seasonCompactLabel}>MUSIM YANG DITAMPILKAN</Text>
              <Text style={s.seasonCompactTitle} numberOfLines={1}>{selectedSeasonLabel}</Text>
              <Text style={s.seasonCompactRange} numberOfLines={1}>{selectedSeasonRange}</Text>
            </View>
            <Text style={s.seasonCompactAction}>{showSeasonFilter ? 'Tutup' : 'Ganti'}</Text>
          </TouchableOpacity>

          {showSeasonFilter && (
            <View style={s.seasonDrawer}>
              {seasonLoading ? (
                <View style={s.seasonLoadingRow}>
                  <ActivityIndicator size="small" color={T.green} />
                  <Text style={s.seasonLoadingText}>Memuat musim...</Text>
                </View>
              ) : seasons.length ? (
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.seasonChipRow}>
                  {seasons.map((season) => {
                    const sid = String(season.season_id);
                    const isActive = season.end_date == null;
                    const isSelected = sid === String(selectedSeasonId);
                    return (
                      <TouchableOpacity
                        key={sid}
                        style={[s.seasonChip, isSelected && s.seasonChipActive]}
                        onPress={() => setSelectedSeasonId(sid)}
                        activeOpacity={0.9}
                      >
                        <Text style={[s.seasonChipText, isSelected && s.seasonChipTextActive]} numberOfLines={1}>
                          {getSeasonName(season)}
                        </Text>
                        <Text style={[s.seasonChipRange, isSelected && s.seasonChipRangeActive]} numberOfLines={1}>
                          {getSeasonRangeLabel(season)}
                        </Text>
                        {isActive && <Text style={[s.seasonChipBadge, isSelected && s.seasonChipBadgeActive]}>Aktif</Text>}
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              ) : (
                <Text style={s.seasonLoadingText}>Belum ada musim tersedia.</Text>
              )}
              {!!activeSeason && String(activeSeason.season_id) !== String(selectedSeasonId) && (
                <TouchableOpacity
                  style={[s.smallBtn, { alignSelf: 'flex-start', marginTop: 10 }]}
                  onPress={() => setSelectedSeasonId(String(activeSeason.season_id))}
                  activeOpacity={0.85}
                >
                  <Text style={s.smallBtnText}>Kembali ke musim aktif</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>

        {!!errorText && (
          <View style={s.errBox}>
            <Text style={s.errText}>{errorText}</Text>
          </View>
        )}

        {(drafts || []).length > 0 && (
          <View style={{ marginBottom: 12 }}>
            <Text style={s.sectionTitle}>Draft dari Scan</Text>
            <Text style={s.sectionSub}>Pilih lahan lalu confirm biar masuk riwayat.</Text>
            <View style={{ marginTop: 8, gap: 10 }}>
              {drafts.map((d) => (
                <DraftCard key={d.id} d={d} />
              ))}
            </View>
          </View>
        )}


        <View style={s.listHeader}>
          <View style={{ flex: 1 }}>
            <Text style={s.listEyebrow}>DAFTAR LAHAN</Text>
            <Text style={s.listTitle}>
              {tab === 'nursery'
                ? 'Fase Pembibitan'
                : tab === 'ready'
                  ? 'Siap Ditanam'
                  : tab === 'running'
                    ? 'Sedang Berjalan'
                    : 'Selesai Panen'}
            </Text>
          </View>
          <View style={s.listCountBadge}>
            <Text style={s.listCountValue}>{filteredFields.length}</Text>
            <Text style={s.listCountLabel}>lahan</Text>
          </View>
        </View>

        {loading ? (
          <View style={{ paddingVertical: 20 }}>
            <ActivityIndicator />
          </View>
        ) : filteredFields.length === 0 ? (
          <View style={s.emptyBox}>
            <Text style={s.emptyTitle}>Tidak ada lahan</Text>
            <Text style={s.emptySub}>
              {tab === 'nursery'
                ? 'Belum ada lahan di fase pembibitan.'
                : tab === 'ready'
                ? 'Belum ada lahan siap tanam.'
                : tab === 'running'
                ? 'Belum ada lahan berjalan pada filter ini.'
                : 'Belum ada data lahan panen pada filter ini.'}
            </Text>
          </View>
        ) : (
          <View style={{ gap: 12 }}>
            {filteredFields.map((f) => (
              <FieldCard key={f.id} f={f} />
            ))}
          </View>
        )}

        <View style={s.tipBox}>
          <Text style={s.tipTitle}>Catatan</Text>
          <Text style={s.tipText}>
            Alur kerja: Pembibitan (HSS) → Siap Tanam → Berjalan (HST) → Panen. Setelah panen, lihat rekap di Report.
          </Text>
        </View>
      </ScrollView>

      <Modal visible={showAddRiceNote} transparent animationType="fade" onRequestClose={() => setShowAddRiceNote(false)}>
        <ModalSheet stylesRef={s}>
          <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            <Text style={s.modalTitle}>Tulis Catatan Padi</Text>
            <Text style={s.modalSub}>Catatan akan muncul kembali sesuai varietas, lahan, dan waktu yang dipilih.</Text>

            <Text style={s.label}>Judul</Text>
            <TextInput
              value={rnTitle}
              onChangeText={setRnTitle}
              placeholder="contoh: Jaga air setelah pemupukan"
              placeholderTextColor={T.muted}
              style={s.input}
            />

            <Text style={s.label}>Isi catatan</Text>
            <TextInput
              value={rnBody}
              onChangeText={setRnBody}
              placeholder="Tulis pengingat perawatan yang penting..."
              placeholderTextColor={T.muted}
              style={[s.input, s.riceNoteBodyInput]}
              multiline
              textAlignVertical="top"
            />

            <Text style={s.label}>Varietas</Text>
            <TouchableOpacity
              style={[s.chip, !rnVariety && s.chipActive, { alignSelf: 'flex-start', marginBottom: 7 }]}
              onPress={() => setRnVariety('')}
            >
              <Text style={[s.chipText, !rnVariety && s.chipTextActive]}>Semua varietas</Text>
            </TouchableOpacity>
            <TextInput
              value={rnVariety}
              onChangeText={setRnVariety}
              placeholder="Kosongkan untuk semua varietas"
              placeholderTextColor={T.muted}
              style={s.input}
            />
            <VarietySelector value={rnVariety} onChange={setRnVariety} />

            <Text style={s.label}>Tampilkan kapan?</Text>
            <View style={s.pickerRow}>
              {RICE_NOTE_PHASE_OPTIONS.map((option) => (
                <TouchableOpacity
                  key={option.key}
                  style={[s.chip, rnPhase === option.key && s.chipActive]}
                  onPress={() => setRnPhase(option.key)}
                >
                  <Text style={[s.chipText, rnPhase === option.key && s.chipTextActive]}>{option.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {rnPhase === 'hst' && (
              <View style={s.riceNoteHstRow}>
                <View style={{ flex: 1 }}>
                  <Text style={s.label}>Mulai HST</Text>
                  <TextInput value={rnHstFrom} onChangeText={setRnHstFrom} keyboardType="numeric" style={s.input} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={s.label}>Sampai HST</Text>
                  <TextInput value={rnHstTo} onChangeText={setRnHstTo} keyboardType="numeric" style={s.input} />
                </View>
              </View>
            )}

            <Text style={s.label}>Prioritas</Text>
            <View style={s.pickerRow}>
              {RICE_NOTE_PRIORITY_OPTIONS.map((option) => (
                <TouchableOpacity
                  key={option.key}
                  style={[s.chip, rnPriority === option.key && s.chipActive]}
                  onPress={() => setRnPriority(option.key)}
                >
                  <Text style={[s.chipText, rnPriority === option.key && s.chipTextActive]}>{option.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={s.label}>Berlaku untuk lahan</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.chipRow}>
              <TouchableOpacity style={[s.chip, rnFieldId == null && s.chipActive]} onPress={() => setRnFieldId(null)}>
                <Text style={[s.chipText, rnFieldId == null && s.chipTextActive]}>Semua lahan</Text>
              </TouchableOpacity>
              {selectableFields.map((field) => (
                <TouchableOpacity
                  key={field.id}
                  style={[s.chip, Number(rnFieldId) === Number(field.id) && s.chipActive]}
                  onPress={() => setRnFieldId(field.id)}
                >
                  <Text style={[s.chipText, Number(rnFieldId) === Number(field.id) && s.chipTextActive]}>
                    {field.name || `#${field.id}`}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <View style={s.modalActionsRow}>
              <TouchableOpacity style={[s.btn, s.btnGhost, { flex: 1 }]} onPress={() => setShowAddRiceNote(false)}>
                <Text style={s.btnTextDark}>Batal</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.btn, s.btnPrimary, { flex: 1, opacity: rnSaving ? 0.7 : 1 }]}
                disabled={rnSaving}
                onPress={saveRiceNote}
              >
                <Text style={s.btnTextWhite}>{rnSaving ? 'Menyimpan...' : 'Simpan Catatan'}</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </ModalSheet>
      </Modal>

      <Modal
        visible={imgPreview.open}
        transparent
        animationType="fade"
        onRequestClose={closeImagePreview}
      >
        <View style={s.previewMask}>
          <TouchableOpacity style={StyleSheet.absoluteFillObject} onPress={closeImagePreview} />
          <View style={s.previewCard}>
            <View style={s.previewHeader}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={s.previewTitle} numberOfLines={1}>{imgPreview.title || 'Perkembangan Padi'}</Text>
                {!!imgPreview.subtitle && <Text style={s.previewSubtitle}>{imgPreview.subtitle}</Text>}
              </View>
              <View style={s.previewCounter}>
                <Text style={s.previewCounterText}>
                  {(imgPreview.index || 0) + 1} / {imgPreview.uris?.length || 1}
                </Text>
              </View>
              <TouchableOpacity style={s.previewXButton} onPress={closeImagePreview}>
                <Text style={s.previewXText}>×</Text>
              </TouchableOpacity>
            </View>

            <View style={s.previewImageStage}>
              <Image
                source={{ uri: imgPreview.uris?.[imgPreview.index] }}
                style={s.previewImg}
                resizeMode="contain"
              />
              {(imgPreview.uris?.length || 0) > 1 && (
                <>
                  <TouchableOpacity style={[s.previewNavButton, s.previewNavLeft]} onPress={() => moveImagePreview(-1)}>
                    <Text style={s.previewNavText}>‹</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[s.previewNavButton, s.previewNavRight]} onPress={() => moveImagePreview(1)}>
                    <Text style={s.previewNavText}>›</Text>
                  </TouchableOpacity>
                </>
              )}
            </View>

            {(imgPreview.uris?.length || 0) > 1 && (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={s.previewThumbRail}
              >
                {imgPreview.uris.map((uri, index) => (
                  <TouchableOpacity
                    key={uri}
                    style={[s.previewThumbButton, index === imgPreview.index && s.previewThumbButtonActive]}
                    onPress={() => setImgPreview((current) => ({ ...current, index }))}
                  >
                    <Image source={{ uri }} style={s.previewThumbImage} resizeMode="cover" />
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}

            <TouchableOpacity style={s.previewCloseBtn} onPress={closeImagePreview}>
              <Text style={s.previewCloseText}>Tutup Galeri</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal visible={showArchivedFields} transparent animationType="fade" onRequestClose={() => setShowArchivedFields(false)}>
        <ModalSheet stylesRef={s} cardStyle={s.archiveModalCard}>
          <View style={s.archiveModalHead}>
            <View style={s.archiveTitleWrap}>
              <View style={s.archiveIconWrap}>
                <PadiProIcon name="archive" size={18} color={T.greenDark} strokeWidth={2} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={s.modalTitle}>Lahan Diarsipkan</Text>
                <Text style={s.archiveModalSub}>Tidak tampil di operasional aktif, tetapi seluruh histori tetap aman.</Text>
              </View>
            </View>
            <TouchableOpacity style={s.archiveCloseBtn} onPress={() => setShowArchivedFields(false)}>
              <Text style={s.archiveCloseText}>Tutup</Text>
            </TouchableOpacity>
          </View>

          <View style={s.archiveInfoBox}>
            <Text style={s.archiveInfoTitle}>Arsip bukan hapus permanen</Text>
            <Text style={s.archiveInfoText}>Lahan dapat diaktifkan kembali kapan saja tanpa kehilangan hasil panen, biaya, atau kegiatan lama.</Text>
          </View>

          <ScrollView style={s.archiveList} contentContainerStyle={s.archiveListContent} showsVerticalScrollIndicator={false}>
            {archivedFields.length === 0 ? (
              <View style={s.archiveEmpty}>
                <Text style={s.archiveEmptyTitle}>Belum ada lahan diarsipkan</Text>
                <Text style={s.archiveEmptyText}>Lahan yang sudah tidak digunakan akan tersimpan di sini.</Text>
              </View>
            ) : archivedFields.map((field) => {
              const fieldId = Number(field.id ?? field.field_id);
              const historyCount = Number(field.harvest_count || 0) + Number(field.expense_count || 0) + Number(field.cycle_count || 0);
              return (
                <View key={fieldId} style={s.archiveFieldCard}>
                  <View style={s.archiveFieldTop}>
                    <View style={{ flex: 1 }}>
                      <Text style={s.archiveFieldName}>{field.name}</Text>
                      <Text style={s.archiveFieldMeta}>
                        {Number(field.area_rante || 0).toLocaleString('id-ID')} rante
                        {field.archived_at ? ` · Diarsipkan ${fmtDateShort(field.archived_at)}` : ''}
                      </Text>
                    </View>
                    <View style={s.archiveBadge}><Text style={s.archiveBadgeText}>ARSIP</Text></View>
                  </View>
                  <Text style={s.archiveHistoryText}>{historyCount} catatan histori tetap tersimpan</Text>
                  <TouchableOpacity
                    style={[s.restoreBtn, archiveSaving[fieldId] && { opacity: .55 }]}
                    disabled={!!archiveSaving[fieldId]}
                    onPress={() => setFieldArchived(field, false)}
                  >
                    <Text style={s.restoreBtnText}>{archiveSaving[fieldId] ? 'Memproses...' : 'Aktifkan Kembali'}</Text>
                  </TouchableOpacity>
                </View>
              );
            })}
          </ScrollView>
        </ModalSheet>
      </Modal>

      <Modal visible={showAddField} transparent animationType="fade" onRequestClose={() => setShowAddField(false)}>
        <ModalSheet stylesRef={s}>
            <Text style={s.modalTitle}>Tambah Lahan</Text>

            <Text style={s.label}>Nama</Text>
            <TextInput
              value={nfName}
              onChangeText={setNfName}
              placeholder="contoh: Lahan Belakang Rumah"
              placeholderTextColor={T.muted}
              style={s.input}
            />

            <Text style={s.label}>Varietas</Text>
            <TextInput
              value={nfSeed}
              onChangeText={setNfSeed}
              placeholder="contoh: Inpari 32"
              placeholderTextColor={T.muted}
              style={s.input}
            />
            <VarietySelector value={nfSeed} onChange={setNfSeed} />

            <Text style={s.label}>Tanggal Tanam (YYYY-MM-DD)</Text>
            <TextInput value={nfPlant} onChangeText={setNfPlant} placeholderTextColor={T.muted} style={s.input} />

            <Text style={s.label}>Target Panen (hari)</Text>
            <TextInput value={nfHarvestDays} onChangeText={setNfHarvestDays} keyboardType="numeric" style={s.input} />

            <View style={s.modalActionsRow}>
              <TouchableOpacity style={[s.btn, s.btnGhost, { flex: 1 }]} onPress={() => setShowAddField(false)}>
                <Text style={s.btnTextDark}>Batal</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.btn, s.btnPrimary, { flex: 1, opacity: creating ? 0.7 : 1 }]}
                disabled={creating}
                onPress={onCreateField}
              >
                <Text style={s.btnTextWhite}>{creating ? 'Menyimpan...' : 'Simpan'}</Text>
              </TouchableOpacity>
            </View>
        </ModalSheet>
      </Modal>

      <Modal visible={showActivity} transparent animationType="fade" onRequestClose={() => setShowActivity(false)}>
        <ModalSheet stylesRef={s}>
            <Text style={s.modalTitle}>Tambah Kegiatan</Text>
            <Text style={s.modalSub}>
              {actField?.name || ''} {actField?.seed_variety ? `· ${actField.seed_variety}` : ''}
            </Text>

            <Text style={s.label}>Tipe</Text>
            <View style={s.pickerRow}>
              {['semprot', 'pupuk', 'air', 'gulma', 'catatan'].map((t) => {
                const isActive = actType === t;
                const meta = ActivityStyle(t);
                return (
                  <TouchableOpacity key={t} style={[s.chip, isActive && s.chipActive]} onPress={() => setActType(t)}>
                    <View style={s.chipInner}>
                      <PadiProIcon
                        name={meta.glyph}
                        size={13}
                        color={isActive ? '#fff' : meta.dot}
                        strokeWidth={1.9}
                      />
                      <Text style={[s.chipText, isActive && s.chipTextActive]}>{t}</Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>

            <Text style={s.label}>Subtype (opsional)</Text>
            <TextInput
              value={actSubtype}
              onChangeText={setActSubtype}
              placeholder="mis: fungisida / insektisida"
              placeholderTextColor={T.muted}
              style={s.input}
            />

            <Text style={s.label}>Tanggal (YYYY-MM-DD)</Text>
            <TextInput value={actDate} onChangeText={setActDate} placeholderTextColor={T.muted} style={s.input} />

            <Text style={s.label}>Catatan / Produk</Text>
            <TextInput
              value={actNote}
              onChangeText={setActNote}
              placeholder="mis: Antracol 1kg + Decis 250ml"
              placeholderTextColor={T.muted}
              style={s.input}
            />

            <View style={{ flexDirection: 'row', gap: 10 }}>
              <View style={{ flex: 1 }}>
                <Text style={s.label}>Qty</Text>
                <TextInput
                  value={actQty}
                  onChangeText={setActQty}
                  keyboardType="numeric"
                  placeholder="mis: 20"
                  placeholderTextColor={T.muted}
                  style={s.input}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={s.label}>Unit</Text>
                <TextInput
                  value={actUnit}
                  onChangeText={setActUnit}
                  placeholder="ml / g / l"
                  placeholderTextColor={T.muted}
                  style={s.input}
                />
              </View>
            </View>

            <View style={s.modalActionsRow}>
              <TouchableOpacity style={[s.btn, s.btnGhost, { flex: 1 }]} onPress={() => setShowActivity(false)}>
                <Text style={s.btnTextDark}>Batal</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.btn, s.btnPrimary, { flex: 1, opacity: actSaving ? 0.7 : 1 }]}
                disabled={actSaving}
                onPress={saveActivity}
              >
                <Text style={s.btnTextWhite}>{actSaving ? 'Menyimpan...' : 'Simpan'}</Text>
              </TouchableOpacity>
            </View>
        </ModalSheet>
      </Modal>

      <Modal visible={showNursery} transparent animationType="fade" onRequestClose={() => setShowNursery(false)}>
        <ModalSheet stylesRef={s}>
            <Text style={s.modalTitle}>Catat Tabur Bibit</Text>
            <Text style={s.modalSub}>Simpan tanggal tabur untuk mulai hitung HSS dan jadwal siap tanam.</Text>

            <Text style={s.label}>Pilih Lahan</Text>
            <View style={s.fieldPickerPanel}>
              <View style={s.fieldPickerTop}>
                <Text style={s.fieldPickerMeta}>{filteredSelectableFields.length} lahan ditemukan</Text>
                {!!fieldPickerQuery && (
                  <TouchableOpacity style={s.pickerResetBtn} onPress={() => setFieldPickerQuery('')} activeOpacity={0.85}>
                    <Text style={s.pickerResetText}>Reset</Text>
                  </TouchableOpacity>
                )}
              </View>

              <View style={s.fieldSearchBox}>
                <PadiProIcon name="default" size={13} color={T.muted} strokeWidth={2} />
                <TextInput
                  value={fieldPickerQuery}
                  onChangeText={setFieldPickerQuery}
                  placeholder="Cari nama lahan / varietas"
                  placeholderTextColor={T.muted}
                  style={s.fieldSearchInput}
                />
              </View>

              <ScrollView style={s.fieldPickerScroll} contentContainerStyle={s.fieldPickerWrap}>
                {filteredSelectableFields.length ? (
                  filteredSelectableFields.map((f) => (
                    <TouchableOpacity
                      key={f.id}
                      style={[s.chip, s.chipField, nsField?.id === f.id && s.chipActive]}
                      onPress={() => {
                        setNsField(f);
                        setNsSeedVariety(String(f?.seed_variety || '').trim());
                      }}
                    >
                      <Text style={[s.chipText, nsField?.id === f.id && s.chipTextActive]} numberOfLines={1}>
                        {f.name || `#${f.id}`}
                      </Text>
                    </TouchableOpacity>
                  ))
                ) : (
                  <Text style={s.fieldPickerEmpty}>Lahan tidak ditemukan. Coba kata kunci lain.</Text>
                )}
              </ScrollView>
              <Text style={s.fieldPickerSelected}>
                Terpilih: <Text style={s.fieldPickerSelectedStrong}>{nsField?.name || 'Belum dipilih'}</Text>
              </Text>
            </View>

            <Text style={s.label}>Tanggal Tabur (YYYY-MM-DD)</Text>
            <TextInput value={nsDate} onChangeText={setNsDate} placeholderTextColor={T.muted} style={s.input} />

            <Text style={s.label}>Target Siap Tanam (hari)</Text>
            <TextInput value={nsReadyDays} onChangeText={setNsReadyDays} keyboardType="numeric" style={s.input} />

            <Text style={s.label}>Varietas</Text>
            <TextInput
              value={nsSeedVariety}
              onChangeText={setNsSeedVariety}
              placeholder="contoh: Inpari 32"
              placeholderTextColor={T.muted}
              style={s.input}
            />
            <VarietySelector value={nsSeedVariety} onChange={setNsSeedVariety} />
            <RiceReminderList
              items={getRiceNotesForContext(nsSeedVariety, 'select_seed', null, nsField?.id)}
            />

            <Text style={s.label}>Catatan (opsional)</Text>
            <TextInput
              value={nsNote}
              onChangeText={setNsNote}
              placeholder="contoh: menunggu air / kesiapan tenaga tanam"
              placeholderTextColor={T.muted}
              style={s.input}
            />

            <View style={s.modalActionsRow}>
              <TouchableOpacity style={[s.btn, s.btnGhost, { flex: 1 }]} onPress={() => setShowNursery(false)}>
                <Text style={s.btnTextDark}>Batal</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.btn, s.btnPrimary, { flex: 1, opacity: nsSaving ? 0.7 : 1 }]}
                disabled={nsSaving}
                onPress={saveNursery}
              >
                <Text style={s.btnTextWhite}>{nsSaving ? 'Menyimpan...' : 'Simpan'}</Text>
              </TouchableOpacity>
            </View>
        </ModalSheet>
      </Modal>

      <Modal visible={showStartCycle} transparent animationType="fade" onRequestClose={() => setShowStartCycle(false)}>
        <ModalSheet stylesRef={s}>
            <Text style={s.modalTitle}>Sudah Tanam</Text>
            <Text style={s.modalSub}>Pilih lahan, isi varietas dan umur padi. Sistem mulai siklus baru.</Text>

            <Text style={s.label}>Pilih Lahan</Text>
            <View style={s.fieldPickerPanel}>
              <View style={s.fieldPickerTop}>
                <Text style={s.fieldPickerMeta}>{filteredSelectableFields.length} lahan ditemukan</Text>
                {!!fieldPickerQuery && (
                  <TouchableOpacity style={s.pickerResetBtn} onPress={() => setFieldPickerQuery('')} activeOpacity={0.85}>
                    <Text style={s.pickerResetText}>Reset</Text>
                  </TouchableOpacity>
                )}
              </View>

              <View style={s.fieldSearchBox}>
                <PadiProIcon name="default" size={13} color={T.muted} strokeWidth={2} />
                <TextInput
                  value={fieldPickerQuery}
                  onChangeText={setFieldPickerQuery}
                  placeholder="Cari nama lahan / varietas"
                  placeholderTextColor={T.muted}
                  style={s.fieldSearchInput}
                />
              </View>

              <ScrollView style={s.fieldPickerScroll} contentContainerStyle={s.fieldPickerWrap}>
                {filteredSelectableFields.length ? (
                  filteredSelectableFields.map((f) => (
                    <TouchableOpacity
                      key={f.id}
                      style={[s.chip, s.chipField, scField?.id === f.id && s.chipActive]}
                      onPress={() => pickStartCycleField(f)}
                    >
                      <Text style={[s.chipText, scField?.id === f.id && s.chipTextActive]} numberOfLines={1}>
                        {f.name || `#${f.id}`}
                      </Text>
                    </TouchableOpacity>
                  ))
                ) : (
                  <Text style={s.fieldPickerEmpty}>Lahan tidak ditemukan. Coba kata kunci lain.</Text>
                )}
              </ScrollView>
              <Text style={s.fieldPickerSelected}>
                Terpilih: <Text style={s.fieldPickerSelectedStrong}>{scField?.name || 'Belum dipilih'}</Text>
              </Text>
            </View>

            <Text style={s.label}>Tanggal Tanam (YYYY-MM-DD)</Text>
            <TextInput value={scPlant} onChangeText={setScPlant} placeholderTextColor={T.muted} style={s.input} />

            <Text style={s.label}>Varietas</Text>
            <TextInput
              value={scSeedVariety}
              onChangeText={setScSeedVariety}
              placeholder="contoh: Inpari 32"
              placeholderTextColor={T.muted}
              style={s.input}
            />
            <VarietySelector value={scSeedVariety} onChange={setScSeedVariety} />
            <RiceReminderList
              items={getRiceNotesForContext(scSeedVariety, 'planting', null, scField?.id)}
            />

            <Text style={s.label}>Umur padi (hari panen)</Text>
            <TextInput value={scHarvestDays} onChangeText={setScHarvestDays} keyboardType="numeric" style={s.input} />

            <Text style={s.label}>Siklus semprot (hari)</Text>
            <TextInput value={scCycleDays} onChangeText={setScCycleDays} keyboardType="numeric" style={s.input} />

            <Text style={s.label}>Catatan (opsional)</Text>
            <TextInput
              value={scNotes}
              onChangeText={setScNotes}
              placeholder="opsional..."
              placeholderTextColor={T.muted}
              style={s.input}
            />

            <View style={s.modalActionsRow}>
              <TouchableOpacity style={[s.btn, s.btnGhost, { flex: 1 }]} onPress={() => setShowStartCycle(false)}>
                <Text style={s.btnTextDark}>Batal</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.btn, s.btnPrimary, { flex: 1, opacity: scSaving ? 0.7 : 1 }]}
                disabled={scSaving}
                onPress={startCycle}
              >
                <Text style={s.btnTextWhite}>{scSaving ? 'Menyimpan...' : 'Simpan'}</Text>
              </TouchableOpacity>
            </View>
        </ModalSheet>
      </Modal>

      <Modal visible={showPhoto} transparent animationType="fade" onRequestClose={() => !phUploading && setShowPhoto(false)}>
        <ModalSheet stylesRef={s} cardStyle={s.photoModalCard}>
          <ScrollView
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={s.photoModalContent}
          >
            <View style={s.photoModalHeader}>
              <View style={s.photoModalIcon}>
                <PadiProIcon name="foto" size={20} color={T.greenDark} strokeWidth={2} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={s.modalTitle}>Dokumentasi Perkembangan</Text>
                <Text style={s.photoModalField} numberOfLines={1}>
                  {phField?.name || ''}{phField?.seed_variety ? ' · ' + phField.seed_variety : ''}
                </Text>
              </View>
              <View style={s.photoSelectedCount}>
                <Text style={s.photoSelectedCountValue}>{phAssets.length}</Text>
                <Text style={s.photoSelectedCountLabel}>/ {MAX_OPERATION_PHOTOS}</Text>
              </View>
            </View>

            <Text style={s.photoModalDescription}>
              Simpan beberapa sudut lahan pada tanggal yang sama agar perubahan daun, air, dan pertumbuhan padi mudah dibandingkan.
            </Text>

            <Text style={s.label}>Tanggal pengamatan</Text>
            <TextInput value={phDate} onChangeText={setPhDate} placeholderTextColor={T.muted} style={s.input} />

            <Text style={s.label}>Catatan kondisi</Text>
            <TextInput
              value={phNote}
              onChangeText={setPhNote}
              placeholder="Contoh: daun mulai hijau merata, air cukup..."
              placeholderTextColor={T.muted}
              style={[s.input, s.photoNoteInput]}
              multiline
              textAlignVertical="top"
            />

            <TouchableOpacity
              style={[s.photoPickerButton, phAssets.length >= MAX_OPERATION_PHOTOS && s.photoPickerButtonDisabled]}
              onPress={pickPhotos}
              disabled={phUploading || phAssets.length >= MAX_OPERATION_PHOTOS}
              activeOpacity={0.86}
            >
              <View style={s.photoPickerIcon}>
                <PadiProIcon name="foto" size={18} color={T.greenDark} strokeWidth={2} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={s.photoPickerTitle}>
                  {phAssets.length ? 'Tambah foto lainnya' : 'Pilih beberapa foto'}
                </Text>
                <Text style={s.photoPickerSub}>Maksimal 8 gambar · hingga 6 MB per foto</Text>
              </View>
              <Text style={s.photoPickerAction}>{phAssets.length >= MAX_OPERATION_PHOTOS ? 'Penuh' : 'Pilih'}</Text>
            </TouchableOpacity>

            {phAssets.length === 0 ? (
              <View style={s.photoEmptyState}>
                <Text style={s.photoEmptyTitle}>Belum ada foto dipilih</Text>
                <Text style={s.photoEmptyText}>Pilih foto dari galeri. Anda dapat meninjau dan menghapusnya sebelum disimpan.</Text>
              </View>
            ) : (
              <View style={s.photoSelectionBlock}>
                <View style={s.photoSelectionHead}>
                  <Text style={s.photoSelectionTitle}>Foto terpilih</Text>
                  <Text style={s.photoSelectionMeta}>{phAssets.length} foto siap disimpan</Text>
                </View>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.photoSelectionRail}>
                  {phAssets.map((asset, index) => (
                    <View key={asset.assetId || asset.uri} style={s.photoSelectionItem}>
                      <Image source={{ uri: asset.uri }} style={s.photoSelectionImage} resizeMode="cover" />
                      <View style={s.photoSelectionNumber}>
                        <Text style={s.photoSelectionNumberText}>{index + 1}</Text>
                      </View>
                      <TouchableOpacity
                        style={s.photoRemoveButton}
                        onPress={() => removePickedPhoto(index)}
                        disabled={phUploading}
                      >
                        <Text style={s.photoRemoveText}>×</Text>
                      </TouchableOpacity>
                    </View>
                  ))}
                </ScrollView>
              </View>
            )}

            <View style={s.photoPrivacyNote}>
              <Text style={s.photoPrivacyTitle}>Tersimpan pada timeline lahan</Text>
              <Text style={s.photoPrivacyText}>Semua foto akan dikelompokkan berdasarkan tanggal pengamatan dan dapat dibuka sebagai galeri.</Text>
            </View>

            <View style={s.modalActionsRow}>
              <TouchableOpacity
                style={[s.btn, s.btnGhost, { flex: 1 }]}
                onPress={() => setShowPhoto(false)}
                disabled={phUploading}
              >
                <Text style={s.btnTextDark}>Batal</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.btn, s.btnPrimary, { flex: 1, opacity: phUploading || !phAssets.length ? 0.55 : 1 }]}
                disabled={phUploading || !phAssets.length}
                onPress={uploadPhotos}
              >
                <Text style={s.btnTextWhite}>
                  {phUploading ? 'Mengunggah ' + phAssets.length + ' foto...' : 'Simpan ' + phAssets.length + ' Foto'}
                </Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </ModalSheet>
      </Modal>

      <Modal visible={showAddExpense} transparent animationType="fade" onRequestClose={() => setShowAddExpense(false)}>
        <ModalSheet stylesRef={s}>
            <Text style={s.modalTitle}>Tambah Biaya</Text>
            <Text style={s.modalSub}>
              {exField?.name || ''} {exField?.seed_variety ? `· ${exField.seed_variety}` : ''}
            </Text>

            <Text style={s.label}>Kategori</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {['Pupuk', 'Pestisida', 'Tenaga Kerja', 'Sewa', 'BBM', 'Peralatan', 'Lainnya'].map((c) => (
                <TouchableOpacity
                  key={c}
                  style={[s.chip, String(exCategory).toLowerCase() === c.toLowerCase() && s.chipActive]}
                  onPress={() => setExCategory(c)}
                >
                  <Text style={[s.chipText, String(exCategory).toLowerCase() === c.toLowerCase() && s.chipTextActive]}>
                    {c}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <View style={{ flexDirection: 'row', gap: 10 }}>
              <View style={{ flex: 1 }}>
                <Text style={s.label}>Tanggal</Text>
                <TextInput value={exDate} onChangeText={setExDate} placeholderTextColor={T.muted} style={s.input} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={s.label}>Subkategori</Text>
                <TextInput
                  value={exSubcategory}
                  onChangeText={setExSubcategory}
                  placeholder="opsional"
                  placeholderTextColor={T.muted}
                  style={s.input}
                />
              </View>
            </View>

            <Text style={s.label}>Item</Text>
            <TextInput
              value={exItem}
              onChangeText={setExItem}
              placeholder="contoh: Urea / Antracol / Ongkos tanam"
              placeholderTextColor={T.muted}
              style={s.input}
            />

            <View style={{ flexDirection: 'row', gap: 10 }}>
              <View style={{ flex: 1 }}>
                <Text style={s.label}>Qty</Text>
                <TextInput
                  value={exQty}
                  onChangeText={setExQty}
                  keyboardType="numeric"
                  placeholder="mis: 2"
                  placeholderTextColor={T.muted}
                  style={s.input}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={s.label}>Unit</Text>
                <TextInput
                  value={exUnit}
                  onChangeText={setExUnit}
                  placeholder="kg / liter / hari"
                  placeholderTextColor={T.muted}
                  style={s.input}
                />
              </View>
            </View>

            <View style={{ flexDirection: 'row', gap: 10 }}>
              <View style={{ flex: 1 }}>
                <Text style={s.label}>Harga</Text>
                <TextInput
                  value={exUnitPrice}
                  onChangeText={setExUnitPrice}
                  keyboardType="numeric"
                  placeholder="mis: 120000"
                  placeholderTextColor={T.muted}
                  style={s.input}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={s.label}>Total</Text>
                <TextInput
                  value={exTotal}
                  onChangeText={setExTotal}
                  keyboardType="numeric"
                  placeholder="auto (atau isi manual)"
                  placeholderTextColor={T.muted}
                  style={s.input}
                />
              </View>
            </View>

            <View style={s.previewTotalBox}>
              <Text style={s.previewTotalText}>
                Preview total:{' '}
                <Text style={{ color: T.text, fontWeight: '900' }}>
                  {formatIDR(computeTotalLive(exQty, exUnitPrice, exTotal) || 0)}
                </Text>
              </Text>
            </View>

            <Text style={s.label}>Catatan (opsional)</Text>
            <TextInput
              value={exNotes}
              onChangeText={setExNotes}
              placeholder="mis: beli di kios A"
              placeholderTextColor={T.muted}
              style={s.input}
            />

            <View style={s.modalActionsRow}>
              <TouchableOpacity style={[s.btn, s.btnGhost, { flex: 1 }]} onPress={() => setShowAddExpense(false)}>
                <Text style={s.btnTextDark}>Tutup</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.btn, s.btnPrimary, { flex: 1, opacity: exSaving ? 0.7 : 1 }]}
                disabled={exSaving}
                onPress={saveFieldExpense}
              >
                <Text style={s.btnTextWhite}>{exSaving ? 'Menyimpan...' : 'Simpan'}</Text>
              </TouchableOpacity>
            </View>
        </ModalSheet>
      </Modal>
    </View>
  );
}

const s = StyleSheet.create({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16 },

  header: {
    paddingTop: 10,
    paddingHorizontal: 10,
    paddingBottom: 10,
    backgroundColor: T.bg,
    borderBottomWidth: 1,
    borderBottomColor: T.border,
    gap: 8,
  },
  scrollContent: {
    paddingTop: 10,
  },
  headerIconBtn: {
    width: 36,
    height: 36,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.38)',
  },
  hHint: { marginTop: 2, color: T.muted, fontWeight: '700' },
  overviewCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 9,
    padding: 13,
    borderRadius: 16,
    borderWidth: 1,
    borderLeftWidth: 4,
    borderColor: PADIPRO.borderStrong,
    borderLeftColor: T.green,
    backgroundColor: T.card,
    ...padiproShadow,
  },
  overviewEyebrow: { color: T.greenDark, fontWeight: '900', fontSize: 9, letterSpacing: 1.1 },
  overviewTitle: { marginTop: 3, color: T.text, fontWeight: '900', fontSize: 17 },
  overviewMeta: { marginTop: 4, color: T.muted, fontWeight: '700', fontSize: 11, lineHeight: 16 },
  overviewAttention: {
    width: 76,
    minHeight: 60,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 13,
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  overviewAttentionWarn: { borderColor: PADIPRO.gold, backgroundColor: PADIPRO.goldSoft },
  overviewAttentionValue: { color: T.text, fontWeight: '900', fontSize: 20 },
  overviewAttentionLabel: { marginTop: 1, color: T.muted, fontWeight: '800', fontSize: 9.5 },
  quickActionScroll: { marginBottom: 10 },
  quickActionRail: { gap: 7, paddingRight: 8 },
  quickAction: {
    minHeight: 42,
    paddingHorizontal: 11,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: PADIPRO.borderStrong,
    backgroundColor: T.card,
  },
  quickActionPrimary: { backgroundColor: T.green, borderColor: T.greenDark },
  quickActionIcon: {
    width: 26,
    height: 26,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    backgroundColor: T.greenSoft,
  },
  quickActionIconPrimary: { backgroundColor: 'rgba(255,255,255,0.16)' },
  quickActionText: { color: T.text, fontWeight: '900', fontSize: 11.5 },
  quickActionTextPrimary: { color: '#fff' },
  heroStrip: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 8,
  },
  heroPill: {
    flex: 1,
    minWidth: 0,
    backgroundColor: PADIPRO.goldSoft,
    borderWidth: 1,
    borderColor: PADIPRO.gold,
    borderRadius: 10,
    paddingHorizontal: 9,
    paddingVertical: 7,
  },
  heroPillLabel: { color: PADIPRO.goldDeep, fontWeight: '800', fontSize: 10 },
  heroPillValue: { marginTop: 1, color: T.greenDark, fontWeight: '900', fontSize: 11.5 },
  headerActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 12 },
  headerAction: {
    flexBasis: '48%',
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    minHeight: 40,
    paddingHorizontal: 8,
  },

  hBtnGhost: {
    backgroundColor: T.greenSoft,
    borderWidth: 1,
    borderColor: T.greenSoft2,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 10,
    ...padiproShadow,
  },
  hBtnGhostText: { fontWeight: '900', color: T.greenDark },

  hBtnPrimary: {
    backgroundColor: T.green,
    borderWidth: 1,
    borderColor: T.greenDark,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 10,
    ...padiproShadow,
  },
  hBtnPrimary2: {
    backgroundColor: T.green,
    borderWidth: 1,
    borderColor: T.greenDark,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 10,
    ...padiproShadow,
  },
  hBtnPrimaryText: { fontWeight: '900', color: '#fff' },

  toolbar: {
    marginBottom: 14,
    padding: 11,
    gap: 9,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: PADIPRO.borderStrong,
    backgroundColor: T.card,
    ...padiproShadow,
  },
  controlHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: T.border,
  },
  controlHeadTitle: {
    color: T.text,
    fontWeight: '900',
    fontSize: 12,
    letterSpacing: 0.2,
  },
  controlHeadMeta: {
    color: T.muted,
    fontWeight: '800',
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  kickerLabel: {
    color: T.muted,
    fontWeight: '800',
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginTop: 2,
    marginBottom: -2,
  },
  searchBox: {
    minHeight: 42,
    backgroundColor: PADIPRO.surfaceSunken,
    borderWidth: 1,
    borderColor: T.border,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 11,
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchIcon: {
    width: 22,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
  },
  searchClearBtn: {
    paddingHorizontal: 9,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: PADIPRO.primarySoft,
    borderWidth: 1,
    borderColor: T.border,
  },
  searchClearText: {
    color: T.greenDark,
    fontWeight: '900',
    fontSize: 10.5,
  },

  compactTabs: { gap: 6, paddingRight: 6 },
  tabs: { flexDirection: 'row', gap: 6 },
  tabBtn: {
    minHeight: 34,
    paddingHorizontal: 12,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: PADIPRO.surfaceSunken,
    borderWidth: 1,
    borderColor: T.border,
  },
  tabBtnWide: {},
  tabBtnActive: { backgroundColor: T.green, borderColor: T.greenDark },
  tabText: { fontWeight: '900', color: T.text, fontSize: 10.5 },
  tabTextActive: { color: '#fff' },
  seasonCompact: {
    minHeight: 56,
    paddingHorizontal: 10,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  seasonCompactActive: { borderColor: T.greenSoft2, backgroundColor: T.greenSoft },
  seasonCompactLabel: { color: T.muted, fontWeight: '900', fontSize: 8.5, letterSpacing: .65 },
  seasonCompactTitle: { marginTop: 2, color: T.text, fontWeight: '900', fontSize: 12 },
  seasonCompactRange: { marginTop: 1, color: T.muted, fontWeight: '700', fontSize: 10 },
  seasonCompactAction: { color: T.greenDark, fontWeight: '900', fontSize: 11 },
  seasonDrawer: { paddingTop: 2 },
  phaseMetricRow: { flexDirection: 'row', gap: 6 },
  phaseMetricCard: {
    flex: 1,
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 9,
    paddingVertical: 7,
    backgroundColor: T.card,
    ...padiproShadow,
  },
  phaseMetricWarn: { borderColor: T.warn, backgroundColor: T.warnSoft },
  phaseMetricDanger: { borderColor: T.danger, backgroundColor: T.dangerSoft },
  phaseMetricSafe: { borderColor: T.greenSoft2, backgroundColor: T.greenSoft },
  phaseMetricValue: { color: T.text, fontWeight: '900', fontSize: 15.5 },
  phaseMetricLabel: { marginTop: 1, color: T.muted, fontWeight: '800', fontSize: 10.5 },
  statusLegendRow: {
    marginTop: 2,
    gap: 6,
  },
  statusLegendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingHorizontal: 2,
  },
  statusLegendDot: {
    width: 7,
    height: 7,
    borderRadius: 99,
  },
  statusLegendText: {
    color: T.muted,
    fontWeight: '700',
    fontSize: 12,
  },
  seasonWrap: {
    backgroundColor: T.card,
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 12,
    padding: 9,
    ...padiproShadow,
  },
  seasonTop: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  seasonTitle: { fontWeight: '900', color: T.text, fontSize: 12.5 },
  seasonSub: { marginTop: 2, color: T.greenDark, fontWeight: '800' },
  seasonSubSoft: { marginTop: 2, color: T.muted, fontWeight: '700', fontSize: 11.5 },
  seasonLoadingRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  seasonLoadingText: { color: T.muted, fontWeight: '700' },
  seasonChipRow: { gap: 6, paddingRight: 4 },
  seasonChip: {
    width: 172,
    backgroundColor: T.card,
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  seasonChipActive: { backgroundColor: T.greenSoft, borderColor: T.greenSoft2, ...padiproShadow },
  seasonChipText: { color: T.text, fontWeight: '900' },
  seasonChipTextActive: { color: T.greenDark },
  seasonChipRange: { marginTop: 2, color: T.muted, fontWeight: '700', fontSize: 11 },
  seasonChipRangeActive: { color: T.greenDark },
  seasonChipBadge: {
    marginTop: 5,
    color: T.green,
    fontWeight: '900',
    fontSize: 10.5,
    alignSelf: 'flex-start',
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: T.greenSoft2,
    backgroundColor: T.greenSoft,
    overflow: 'hidden',
  },
  seasonChipBadgeActive: { color: T.greenDark, borderColor: T.green, backgroundColor: T.greenSoft2 },

  card: {
    backgroundColor: T.card,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: PADIPRO.borderStrong,
    padding: 12,
    ...padiproShadow,
  },

  cardTop: { flexDirection: 'row', gap: 10, alignItems: 'flex-start' },
  cardTitle: { fontSize: 15, fontWeight: '900', color: T.text },
  cardSub: { marginTop: 3, color: T.muted, fontWeight: '700' },
  cardSubStrong: { fontWeight: '900', color: T.text },

  badge: {
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
  },
  badgeText: { fontWeight: '900', fontSize: 12 },

  phTop: { flexDirection: 'row', alignItems: 'center', gap: 10, justifyContent: 'space-between', marginTop: 8 },
  phPill: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: T.greenSoft,
    borderWidth: 1,
    borderColor: T.greenSoft2,
  },
  phPillText: { fontWeight: '900', color: T.greenDark },
  phStage: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 6 },
  phStageText: { color: T.text, fontWeight: '800' },
  phMeta: { color: T.muted, fontWeight: '700' },
  phMetaStrong: { color: T.text, fontWeight: '900' },
  phBar: {
    height: 8,
    marginTop: 9,
    borderRadius: 999,
    backgroundColor: PADIPRO.surfaceSunken,
    overflow: 'hidden',
  },
  phFill: { height: 8 },
  phBottom: { marginTop: 8, flexDirection: 'row', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' },
  phBottomText: { color: T.muted, fontWeight: '700' },
  phBottomStrong: { color: T.text, fontWeight: '900' },
  nurseryBox: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 12,
    backgroundColor: PADIPRO.surfaceSunken,
    padding: 10,
    gap: 4,
  },
  nurseryTitle: { color: T.text, fontWeight: '900', fontSize: 13 },
  nurseryMeta: { color: T.muted, fontWeight: '700' },
  nurseryStrong: { color: T.text, fontWeight: '900' },

  harvestAutoBox: { marginTop: 10, padding: 11, borderRadius: 14, borderWidth: 1, borderColor: PADIPRO.primarySoftStrong, backgroundColor: PADIPRO.primarySoft },
  harvestAutoHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  harvestAutoTitle: { color: T.greenDark, fontWeight: '900', fontSize: 12 },
  harvestAutoHstBadge: { paddingVertical: 4, paddingHorizontal: 8, borderRadius: 999, backgroundColor: '#fff', borderWidth: 1, borderColor: PADIPRO.primarySoftStrong },
  harvestAutoHstText: { color: T.greenDark, fontWeight: '900', fontSize: 11 },
  harvestAutoMeta: { color: T.textSoft, fontWeight: '700', fontSize: 11, marginTop: 4 },
  harvestAutoStrong: { color: T.text, fontWeight: '900' },
  infoRow: { marginTop: 8, flexDirection: 'row', gap: 8, alignItems: 'center', justifyContent: 'space-between' },
  infoText: { color: T.muted, fontWeight: '800', marginTop: 2 },
  infoStrong: { color: T.text, fontWeight: '900' },

  fieldStats: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'stretch',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: PADIPRO.surfaceSunken,
    overflow: 'hidden',
  },
  fieldStatItem: { flex: 1, minWidth: 0, paddingHorizontal: 8, paddingVertical: 9 },
  fieldStatDivider: { width: 1, backgroundColor: T.border, marginVertical: 7 },
  fieldStatLabel: { color: T.muted, fontWeight: '900', fontSize: 8.5, letterSpacing: .55 },
  fieldStatValue: { marginTop: 3, color: T.text, fontWeight: '900', fontSize: 11.5 },
  fieldCostHint: { marginTop: 5, color: T.muted, fontWeight: '700', fontSize: 10.5 },
  cardActionRail: { gap: 7, paddingTop: 9, paddingRight: 8 },
  cardActionBtn: {
    minHeight: 36,
    paddingHorizontal: 11,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    borderWidth: 1,
  },
  cardActionPrimary: { backgroundColor: T.green, borderColor: T.greenDark },
  cardActionGhost: { backgroundColor: PADIPRO.surfaceSunken, borderColor: T.border },
  cardActionWarn: { backgroundColor: T.warnSoft, borderColor: T.warn },
  cardActionDanger: { backgroundColor: T.danger, borderColor: T.danger },
  cardActionArchive: { backgroundColor: T.dangerSoft, borderColor: T.danger },
  smallBtn: {
    borderWidth: 1,
    borderColor: T.greenSoft2,
    backgroundColor: T.greenSoft,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 999,
  },
  smallBtnActive: { backgroundColor: T.green, borderColor: T.greenDark },
  smallBtnText: { fontWeight: '900', color: T.greenDark },
  smallBtnTextActive: { color: '#fff' },

  costBox: {
    marginTop: 10,
    padding: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: T.greenSoft2,
    backgroundColor: T.greenSoft,
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  costIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 999,
    backgroundColor: T.greenSoft,
    borderWidth: 1,
    borderColor: T.greenSoft2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  costText: { color: T.muted, fontWeight: '800' },
  costStrong: { color: T.text, fontWeight: '900' },
  costMuted: { color: T.muted, fontWeight: '900' },
  costMeta: { marginTop: 4, color: T.muted, fontWeight: '700', fontSize: 12 },
  costMetaStrong: { color: T.greenDark, fontWeight: '900' },

  actionsRow: { marginTop: 8, flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  btn: { paddingVertical: 8, paddingHorizontal: 10, borderRadius: 10, borderWidth: 1 },
  btnPrimary: { backgroundColor: T.green, borderColor: T.greenDark },
  btnDanger: { backgroundColor: T.danger, borderColor: T.danger },
  btnWarn: { backgroundColor: T.warnSoft, borderColor: T.warn },
  btnGhost: { backgroundColor: T.greenSoft, borderColor: T.greenSoft2 },
  btnArchive: { backgroundColor: T.dangerSoft, borderColor: T.danger },
  btnArchiveText: { color: T.danger, fontWeight: '900' },
  btnTextWhite: { fontWeight: '900', color: '#fff' },
  btnTextDark: { fontWeight: '900', color: T.text },

  segment: { flexDirection: 'row', gap: 10, marginBottom: 8 },
  segBtn: {
    flex: 1,
    backgroundColor: T.card,
    borderWidth: 1,
    borderColor: T.border,
    paddingVertical: 10,
    borderRadius: 14,
    alignItems: 'center',
  },
  segBtnActive: { backgroundColor: T.green, borderColor: T.greenDark },
  segText: { fontWeight: '900', color: T.text },
  segTextActive: { color: '#fff' },

  emptyInline: { color: T.muted, fontWeight: '700' },

  tlRow: { flexDirection: 'row', gap: 10, paddingTop: 10 },
  tlRail: { width: 22, alignItems: 'center' },
  tlDot: { width: 10, height: 10, borderRadius: 99, marginTop: 10 },
  tlLine: { width: 2, flex: 1, marginTop: 6, borderRadius: 2, backgroundColor: T.greenSoft2 },

  tlCard: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 16,
    padding: 12,
    ...padiproShadow,
  },

  tlHead: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },
  tlHeadLeft: {
    flex: 1,
    flexDirection: 'row',
    gap: 10,
    alignItems: 'flex-start',
  },
  tlIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.75)',
    borderWidth: 1,
    borderColor: 'rgba(11,42,26,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tlTitle: { fontWeight: '900', color: T.text, fontSize: 14 },
  tlSub: { color: T.muted, marginTop: 2, fontWeight: '700', fontSize: 12.5 },

  tlCountPill: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.88)',
    borderWidth: 1,
    borderColor: 'rgba(11,42,26,0.08)',
  },
  tlCountText: { fontWeight: '900', color: T.greenDark, fontSize: 12 },

  tlLinesWrap: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: 'rgba(11,42,26,0.06)',
    gap: 8,
  },
  tlBulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  tlBulletDot: {
    width: 7,
    height: 7,
    borderRadius: 99,
    backgroundColor: PADIPRO.primarySoftStrong,
    marginTop: 6,
  },
  tlBulletText: {
    flex: 1,
    color: T.muted,
    fontWeight: '700',
    lineHeight: 18,
    fontSize: 13,
  },

  photoWrap: {
    marginTop: 10,
    borderRadius: 14,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: T.card,
  },
  photoImg: { width: '100%', height: 210, backgroundColor: T.greenSoft },
  photoOpenBar: {
    minHeight: 38,
    paddingHorizontal: 11,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: T.card,
  },
  photoHint: { color: T.greenDark, fontWeight: '900', fontSize: 11 },
  photoOpenArrow: { color: T.greenDark, fontWeight: '900', fontSize: 20 },
  photoGalleryBlock: { marginTop: 10 },
  photoGalleryRail: { gap: 8, paddingRight: 8 },
  photoThumbButton: {
    width: 118,
    height: 96,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: T.card,
  },
  photoThumb: { width: '100%', height: '100%', backgroundColor: T.greenSoft },
  photoIndexBadge: {
    position: 'absolute',
    right: 6,
    bottom: 6,
    minWidth: 22,
    height: 22,
    paddingHorizontal: 6,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(18,48,31,0.86)',
  },
  photoIndexText: { color: '#fff', fontWeight: '900', fontSize: 10 },
  photoGalleryHint: { marginTop: 7, color: T.muted, fontWeight: '700', fontSize: 10.5 },


  costHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 10, justifyContent: 'space-between' },
  costHeaderTitle: { fontWeight: '900', color: T.text },
  costHeaderSub: { color: T.muted, fontWeight: '700', marginTop: 2 },
  costHeaderStrong: { fontWeight: '900', color: T.text },

  emptyMini: {
    marginTop: 10,
    padding: 12,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: T.card,
  },
  emptyMiniTitle: { fontWeight: '900', color: T.text },
  emptyMiniSub: { color: T.muted, fontWeight: '700', marginTop: 4 },

  expItem: { backgroundColor: T.card, borderWidth: 1, borderColor: T.border, borderRadius: 14, padding: 10 },
  expTitle: { fontWeight: '900', color: T.text },
  expSubcat: { color: T.muted, fontWeight: '800' },
  expMeta: { color: T.muted, fontWeight: '700', marginTop: 2 },
  expNotes: { color: T.muted, fontWeight: '700', marginTop: 4 },
  expAmountPill: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: T.greenSoft2,
    backgroundColor: T.greenSoft,
    alignSelf: 'flex-start',
  },
  expAmountText: { fontWeight: '900', color: T.text },

  listHeader: {
    marginBottom: 9,
    paddingHorizontal: 2,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  listEyebrow: { color: T.greenDark, fontWeight: '900', fontSize: 8.5, letterSpacing: 1.15 },
  listTitle: { marginTop: 2, color: T.text, fontWeight: '900', fontSize: 15 },
  listCountBadge: {
    minWidth: 52,
    paddingHorizontal: 9,
    paddingVertical: 6,
    alignItems: 'center',
    borderRadius: 11,
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  listCountValue: { color: T.text, fontWeight: '900', fontSize: 14 },
  listCountLabel: { color: T.muted, fontWeight: '800', fontSize: 8.5 },
  sectionTitle: { fontSize: 16, fontWeight: '900', color: T.text },
  sectionSub: { color: T.muted, marginTop: 2, fontWeight: '700' },

  draftCard: { backgroundColor: T.card, borderRadius: 16, borderWidth: 1, borderColor: T.border, padding: 12 },
  draftTitle: { fontWeight: '900', color: T.text },
  draftSub: { color: T.muted, marginTop: 2, fontWeight: '700' },
  chipRow: { gap: 8, paddingRight: 8 },

  label: { marginTop: 10, marginBottom: 6, color: T.text, fontWeight: '900' },
  input: {
    backgroundColor: T.card,
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: T.text,
    fontWeight: '700',
  },
  pickerRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  varietyPicker: { marginTop: 7 },
  varietyOptions: { gap: 7, paddingRight: 8 },
  varietyOption: {
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: PADIPRO.surfaceSunken,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 7,
  },
  varietyOptionActive: { backgroundColor: T.green, borderColor: T.greenDark },
  varietyOptionText: { color: T.text, fontWeight: '800', fontSize: 12 },
  varietyOptionTextActive: { color: '#fff' },
  varietyReviewText: { marginTop: 6, color: T.warn, fontWeight: '700', fontSize: 12 },

  chip: {
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: T.card,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
  },
  chipInner: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  chipField: { minWidth: 96, alignItems: 'center' },
  chipActive: { backgroundColor: T.green, borderColor: T.greenDark },
  chipText: { fontWeight: '900', color: T.text },
  chipTextActive: { color: '#fff' },
  fieldPickerPanel: {
    backgroundColor: T.card,
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 14,
    padding: 10,
    gap: 8,
  },
  fieldPickerTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  fieldPickerMeta: { color: T.muted, fontWeight: '800', fontSize: 12 },
  pickerResetBtn: {
    borderWidth: 1,
    borderColor: T.greenSoft2,
    backgroundColor: T.greenSoft,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  pickerResetText: { color: T.greenDark, fontWeight: '900', fontSize: 11.5 },
  fieldSearchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 11,
    backgroundColor: T.card,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  fieldSearchInput: { flex: 1, color: T.text, fontWeight: '700' },
  fieldPickerScroll: { maxHeight: 132 },
  fieldPickerWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, paddingRight: 4 },
  fieldPickerEmpty: { color: T.muted, fontWeight: '700', paddingVertical: 4 },
  fieldPickerSelected: { color: T.muted, fontWeight: '700' },
  fieldPickerSelectedStrong: { color: T.text, fontWeight: '900' },

  errBox: {
    backgroundColor: T.dangerSoft,
    borderWidth: 1,
    borderColor: T.danger,
    borderRadius: 14,
    padding: 12,
    marginBottom: 12,
  },
  errText: { color: T.danger, fontWeight: '800' },

  emptyBox: {
    backgroundColor: T.card,
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 16,
    padding: 16,
    marginTop: 4,
    marginBottom: 12,
  },
  emptyTitle: { fontSize: 16, fontWeight: '900', color: T.text },
  emptySub: { color: T.muted, marginTop: 6, fontWeight: '700' },

  tipBox: { marginTop: 14, backgroundColor: T.card, borderWidth: 1, borderLeftWidth: 3, borderColor: T.border, borderLeftColor: PADIPRO.gold, borderRadius: 14, padding: 12 },
  tipTitle: { fontWeight: '900', color: PADIPRO.goldDeep },
  tipText: { color: T.muted, marginTop: 6, fontWeight: '700', lineHeight: 18 },

  riceNotesCard: {
    marginBottom: 11,
    padding: 11,
    borderRadius: 14,
    borderWidth: 1,
    borderLeftWidth: 3,
    borderColor: T.border,
    borderLeftColor: PADIPRO.gold,
    backgroundColor: T.card,
  },
  riceNotesHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  riceNotesHeadActions: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  riceNotesViewBtn: {
    paddingHorizontal: 9,
    paddingVertical: 7,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: T.card,
  },
  riceNotesViewText: { color: T.greenDark, fontWeight: '900', fontSize: 10.5 },
  riceNotePreview: {
    marginTop: 8,
    minHeight: 34,
    paddingHorizontal: 9,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  riceNotePreviewDot: { width: 7, height: 7, borderRadius: 99, backgroundColor: T.green },
  riceNotePreviewDotUrgent: { backgroundColor: T.danger },
  riceNotePreviewText: { flex: 1, color: T.muted, fontWeight: '700', fontSize: 10.5 },
  riceNotePreviewArrow: { color: T.greenDark, fontWeight: '900', fontSize: 18 },
  riceNotesTitleWrap: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 9 },
  riceNotesIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: T.greenSoft,
    borderWidth: 1,
    borderColor: T.greenSoft2,
  },
  riceNotesTitle: { color: T.text, fontWeight: '900', fontSize: 14 },
  riceNotesSub: { marginTop: 1, color: T.muted, fontWeight: '700', fontSize: 10.5 },
  riceNotesAddBtn: {
    paddingHorizontal: 11,
    paddingVertical: 7,
    borderRadius: 10,
    backgroundColor: T.green,
    borderWidth: 1,
    borderColor: T.greenDark,
  },
  riceNotesAddText: { color: '#fff', fontWeight: '900', fontSize: 11 },
  riceNotesList: { marginTop: 9, gap: 7 },
  riceNoteMini: {
    flexDirection: 'row',
    gap: 8,
    padding: 9,
    borderRadius: 11,
    backgroundColor: T.card,
    borderWidth: 1,
    borderColor: T.border,
  },
  riceNoteMiniUrgent: { borderColor: T.danger, backgroundColor: T.dangerSoft },
  riceNoteMiniTitle: { color: T.text, fontWeight: '900', fontSize: 12 },
  riceNoteMiniMeta: { marginTop: 2, color: T.greenDark, fontWeight: '800', fontSize: 10 },
  riceNoteMiniBody: { marginTop: 4, color: T.muted, fontWeight: '700', fontSize: 11, lineHeight: 16 },
  riceNoteArchiveBtn: { alignSelf: 'flex-start', paddingHorizontal: 7, paddingVertical: 5 },
  riceNoteArchiveText: { color: T.danger, fontWeight: '800', fontSize: 10 },
  riceNotesEmpty: { marginTop: 9, color: T.muted, fontWeight: '700', fontSize: 11, lineHeight: 16 },
  riceNotesToggle: { alignSelf: 'flex-start', paddingTop: 8, paddingHorizontal: 2 },
  riceNotesToggleText: { color: T.greenDark, fontWeight: '900', fontSize: 11 },
  riceReminderBox: {
    marginTop: 9,
    padding: 9,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: PADIPRO.gold,
    backgroundColor: PADIPRO.goldSoft,
  },
  riceReminderHead: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 5 },
  riceReminderHeadText: { color: T.greenDark, fontWeight: '900', fontSize: 11 },
  riceReminderItem: { paddingVertical: 5, borderTopWidth: 1, borderTopColor: 'rgba(138,122,46,0.18)' },
  riceReminderTitle: { color: T.text, fontWeight: '900', fontSize: 11 },
  riceReminderBody: { marginTop: 2, color: T.muted, fontWeight: '700', fontSize: 10.5, lineHeight: 15 },
  riceNoteBodyInput: { minHeight: 88, paddingTop: 10 },
  riceNoteHstRow: { flexDirection: 'row', gap: 10 },

  photoModalCard: { maxHeight: '92%' },
  photoModalContent: { paddingBottom: 4 },
  photoModalHeader: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  photoModalIcon: {
    width: 42,
    height: 42,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: T.greenSoft,
    borderWidth: 1,
    borderColor: T.greenSoft2,
  },
  photoModalField: { color: T.greenDark, fontWeight: '800', fontSize: 11.5, marginTop: -3 },
  photoSelectedCount: {
    flexDirection: 'row',
    alignItems: 'baseline',
    paddingHorizontal: 9,
    paddingVertical: 7,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  photoSelectedCountValue: { color: T.text, fontWeight: '900', fontSize: 15 },
  photoSelectedCountLabel: { color: T.muted, fontWeight: '800', fontSize: 10 },
  photoModalDescription: {
    marginTop: 11,
    padding: 10,
    borderRadius: 11,
    color: T.muted,
    fontWeight: '700',
    fontSize: 11,
    lineHeight: 16,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  photoNoteInput: { minHeight: 72, paddingTop: 10 },
  photoPickerButton: {
    marginTop: 12,
    minHeight: 58,
    padding: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 13,
    borderWidth: 1,
    borderColor: T.greenSoft2,
    backgroundColor: T.greenSoft,
  },
  photoPickerButtonDisabled: { opacity: .55 },
  photoPickerIcon: {
    width: 36,
    height: 36,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: T.card,
    borderWidth: 1,
    borderColor: T.greenSoft2,
  },
  photoPickerTitle: { color: T.text, fontWeight: '900', fontSize: 12.5 },
  photoPickerSub: { marginTop: 2, color: T.muted, fontWeight: '700', fontSize: 9.5 },
  photoPickerAction: { color: T.greenDark, fontWeight: '900', fontSize: 11 },
  photoEmptyState: {
    marginTop: 10,
    paddingVertical: 18,
    paddingHorizontal: 14,
    alignItems: 'center',
    borderRadius: 13,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: T.border,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  photoEmptyTitle: { color: T.text, fontWeight: '900', fontSize: 12 },
  photoEmptyText: { marginTop: 4, color: T.muted, fontWeight: '700', fontSize: 10.5, textAlign: 'center', lineHeight: 15 },
  photoSelectionBlock: {
    marginTop: 10,
    padding: 10,
    borderRadius: 13,
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  photoSelectionHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  photoSelectionTitle: { color: T.text, fontWeight: '900', fontSize: 11.5 },
  photoSelectionMeta: { color: T.muted, fontWeight: '800', fontSize: 9.5 },
  photoSelectionRail: { gap: 8, paddingTop: 9, paddingRight: 4 },
  photoSelectionItem: {
    width: 112,
    height: 96,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: T.card,
  },
  photoSelectionImage: { width: '100%', height: '100%', backgroundColor: T.greenSoft },
  photoSelectionNumber: {
    position: 'absolute',
    left: 6,
    bottom: 6,
    minWidth: 22,
    height: 22,
    paddingHorizontal: 6,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 999,
    backgroundColor: 'rgba(18,48,31,0.86)',
  },
  photoSelectionNumberText: { color: '#fff', fontWeight: '900', fontSize: 10 },
  photoRemoveButton: {
    position: 'absolute',
    top: 5,
    right: 5,
    width: 27,
    height: 27,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 999,
    backgroundColor: 'rgba(159,33,56,0.9)',
  },
  photoRemoveText: { color: '#fff', fontWeight: '900', fontSize: 19, lineHeight: 21 },
  photoPrivacyNote: {
    marginTop: 10,
    padding: 10,
    borderRadius: 11,
    borderLeftWidth: 3,
    borderLeftColor: PADIPRO.gold,
    backgroundColor: PADIPRO.goldSoft,
  },
  photoPrivacyTitle: { color: PADIPRO.goldDeep, fontWeight: '900', fontSize: 10.5 },
  photoPrivacyText: { marginTop: 2, color: T.muted, fontWeight: '700', fontSize: 9.5, lineHeight: 14 },

  previewMask: { flex: 1, backgroundColor: 'rgba(2,10,7,0.9)', justifyContent: 'center', padding: 12 },
  previewCard: {
    width: '100%',
    maxWidth: 860,
    maxHeight: '94%',
    alignSelf: 'center',
    borderRadius: 18,
    overflow: 'hidden',
    backgroundColor: '#08110C',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
  },
  previewHeader: {
    minHeight: 58,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    backgroundColor: PADIPRO.primaryDark,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
  },
  previewTitle: { color: '#fff', fontWeight: '900', fontSize: 14 },
  previewSubtitle: { marginTop: 2, color: '#CFE0D2', fontWeight: '700', fontSize: 10.5 },
  previewCounter: {
    paddingHorizontal: 9,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  previewCounterText: { color: '#fff', fontWeight: '900', fontSize: 10.5 },
  previewXButton: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  previewXText: { color: '#fff', fontWeight: '700', fontSize: 23, lineHeight: 25 },
  previewImageStage: { position: 'relative', backgroundColor: '#050906' },
  previewImg: { width: '100%', height: 430, backgroundColor: '#050906' },
  previewNavButton: {
    position: 'absolute',
    top: '44%',
    width: 42,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    backgroundColor: 'rgba(18,48,31,0.82)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  previewNavLeft: { left: 10 },
  previewNavRight: { right: 10 },
  previewNavText: { color: '#fff', fontWeight: '700', fontSize: 35, lineHeight: 38 },
  previewThumbRail: { gap: 8, padding: 10, backgroundColor: '#0C1810' },
  previewThumbButton: {
    width: 58,
    height: 58,
    padding: 2,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
    overflow: 'hidden',
  },
  previewThumbButtonActive: { borderWidth: 2, borderColor: PADIPRO.gold },
  previewThumbImage: { width: '100%', height: '100%', borderRadius: 7, backgroundColor: '#142319' },
  previewCloseBtn: { paddingVertical: 12, alignItems: 'center', backgroundColor: PADIPRO.primaryDark },
  previewCloseText: { color: '#fff', fontWeight: '900' },


  modalMask: { flex: 1, backgroundColor: 'rgba(6,16,10,0.42)', padding: 12, justifyContent: 'center' },
  modalCard: {
    backgroundColor: T.card,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: T.border,
    maxHeight: '95%',
    overflow: 'hidden',
  },
  modalContentNoScroll: {
    flex: 1,
    position: 'relative',
    padding: 15,
    paddingBottom: 16,
  },
  modalTitle: { fontSize: 16, fontWeight: '900', color: T.text, marginBottom: 8 },
  modalSub: { color: T.muted, marginTop: -2, marginBottom: 10, fontWeight: '700' },
  archiveModalCard: { maxHeight: '88%' },
  archiveModalHead: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 },
  archiveTitleWrap: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10 },
  archiveIconWrap: { width: 38, height: 38, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: T.greenSoft, borderWidth: 1, borderColor: T.greenSoft2 },
  archiveModalSub: { color: T.muted, fontWeight: '700', fontSize: 11, lineHeight: 16, marginTop: -4 },
  archiveCloseBtn: { paddingHorizontal: 10, paddingVertical: 7, borderRadius: 9, backgroundColor: PADIPRO.surfaceSunken, borderWidth: 1, borderColor: T.border },
  archiveCloseText: { color: T.text, fontWeight: '900', fontSize: 11 },
  archiveInfoBox: { marginTop: 12, padding: 11, borderRadius: 12, backgroundColor: PADIPRO.goldSoft, borderWidth: 1, borderColor: PADIPRO.gold },
  archiveInfoTitle: { color: PADIPRO.goldDeep, fontWeight: '900', fontSize: 12 },
  archiveInfoText: { color: T.muted, fontWeight: '700', fontSize: 10.5, lineHeight: 15, marginTop: 3 },
  archiveList: { marginTop: 12 },
  archiveListContent: { gap: 9, paddingBottom: 4 },
  archiveEmpty: { alignItems: 'center', paddingVertical: 26, paddingHorizontal: 14, borderRadius: 14, borderWidth: 1, borderStyle: 'dashed', borderColor: T.border, backgroundColor: PADIPRO.surfaceSunken },
  archiveEmptyTitle: { color: T.text, fontWeight: '900', fontSize: 13 },
  archiveEmptyText: { color: T.muted, fontWeight: '700', fontSize: 11, textAlign: 'center', marginTop: 4 },
  archiveFieldCard: { padding: 12, borderRadius: 14, borderWidth: 1, borderColor: T.border, backgroundColor: T.card },
  archiveFieldTop: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  archiveFieldName: { color: T.text, fontWeight: '900', fontSize: 14 },
  archiveFieldMeta: { color: T.muted, fontWeight: '700', fontSize: 10.5, marginTop: 3 },
  archiveBadge: { paddingHorizontal: 8, paddingVertical: 5, borderRadius: 999, backgroundColor: PADIPRO.surfaceSunken, borderWidth: 1, borderColor: T.border },
  archiveBadgeText: { color: T.muted, fontWeight: '900', fontSize: 9 },
  archiveHistoryText: { color: T.greenDark, fontWeight: '800', fontSize: 10.5, marginTop: 9 },
  restoreBtn: { marginTop: 10, minHeight: 42, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: T.green, borderWidth: 1, borderColor: T.greenDark },
  restoreBtnText: { color: '#fff', fontWeight: '900', fontSize: 12 },

  modalActionsRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: T.border,
    backgroundColor: T.card,
  },

  previewTotalBox: {
    marginTop: 10,
    padding: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: T.greenSoft,
  },
  previewTotalText: { color: T.muted, fontWeight: '800' },
});
